/**
 * NavHintsEditor.js
 *
 * Dev-only panel to author custom navigation hint points in the Matterport
 * scene. Gated by either:
 *   - REACT_APP_NAV_HINTS_EDITOR=true   (build-time env flag), or
 *   - URL query param  ?navEdit=1       (runtime toggle for demos)
 *
 * Features:
 *   • List / select / delete custom hint points
 *   • Add point at current camera position (one click)
 *   • Add point via click on scene (uses SDK Renderer intersection)
 *   • Per-point labels in mr / en / hi
 *   • Text color, background color & opacity, font size, arrow direction
 *   • Manual position nudge (X / Y / Z / offsetY)
 *   • Copy JSON to clipboard for persistence into
 *     public/config/navHintsConfig.json
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import navHintsService, {
  SHAPE_PRESETS,
  BILLBOARD_BOARD_SHAPES,
  BILLBOARD_BASE_STANDS,
  DIRECTION_SIGN_VARIANTS,
} from '../services/navHintsService';
import matterportService from '../services/matterportService';
import voiceoverService from '../services/voiceoverService';
import navDirectionSignService from '../services/navDirectionSignService';
import quickNavConfig from '../config/quickNavConfig.json';
import { Z_INDEX } from '../const';
import { useEditMode } from '../utils/editMode';

// Voiceover dropdown options for the Edit-POI panel.
//
// The legacy per-tag voiceovers (read from tagTranslations.json with
// audio at /assets/audio/voiceover/<sid>/<lang>.mp3) have been retired
// in favour of the 13 guided-tour stop narrations, so a POI's
// voiceoverTagId is now the stop's `narrationKey` (e.g.
// "stop_1_mandir_entrance"). voiceoverService.playForTag() detects the
// "stop_" prefix and plays
//   /assets/audio/narration/<narrationKey>_standalone_<lang>.mp3
const VOICEOVER_TAG_OPTIONS = ((quickNavConfig && quickNavConfig.locations) || [])
  .filter((loc) => loc && loc.narrationKey)
  .map((loc) => ({
    sid: loc.narrationKey,
    label: (loc.labels && loc.labels.en) || loc.id || loc.narrationKey,
  }));

const PANEL_WIDTH = 340;
const LANGUAGES = ['mr', 'en', 'hi'];
const LANG_LABELS = { mr: 'मराठी', en: 'English', hi: 'हिंदी' };

const styles = {
  toggleBtn: {
    position: 'fixed',
    top: 72,
    left: 16,
    zIndex: Z_INDEX.NAV_HINTS_EDITOR,
    padding: '10px 16px',
    height: 40,
    borderRadius: 999,
    border: '1px solid rgba(255,215,0,0.35)',
    background: 'rgba(18,6,1,0.92)',
    color: 'rgba(255,215,0,0.95)',
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: '0.01em',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(255,215,0,0.08)',
    fontFamily: "'Noto Sans Devanagari', system-ui, -apple-system, sans-serif",
  },
  panel: {
    position: 'fixed',
    top: 124,
    left: 16,
    width: PANEL_WIDTH,
    maxHeight: 'calc(100vh - 150px)',
    overflowY: 'auto',
    zIndex: Z_INDEX.NAV_HINTS_EDITOR,
    background: 'linear-gradient(160deg, #13050a 0%, #0d0608 100%)',
    borderRadius: 20,
    border: '1px solid rgba(255,215,0,0.22)',
    color: 'rgba(255,255,255,0.88)',
    fontSize: 13,
    fontFamily: "'Noto Sans Devanagari', system-ui, -apple-system, sans-serif",
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    padding: 16,
    boxShadow: '0 24px 60px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,215,0,0.06)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    paddingBottom: 10,
    borderBottom: '1px solid rgba(255,215,0,0.15)',
  },
  title: {
    fontSize: 16, fontWeight: 800,
    color: 'rgba(255,215,0,0.95)', margin: 0,
    letterSpacing: '0.01em',
  },
  closeBtn: {
    width: 32, height: 32, borderRadius: '50%',
    background: 'rgba(255,255,255,0.06)', border: 'none',
    color: 'rgba(255,255,255,0.75)', fontSize: 18,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  btn: {
    padding: '8px 14px', borderRadius: 8, border: '1px solid transparent',
    cursor: 'pointer', fontSize: 12, fontWeight: 700,
    fontFamily: 'inherit', letterSpacing: '0.01em',
    transition: 'background 0.15s, border-color 0.15s',
  },
  btnPrimary: {
    background: 'linear-gradient(180deg, #FFD700 0%, #e6b800 100%)',
    color: '#120601',
    borderColor: 'rgba(255,215,0,0.5)',
    boxShadow: '0 2px 8px rgba(255,215,0,0.25)',
  },
  btnDanger: { background: '#b91c1c', color: '#fff', borderColor: 'rgba(255,255,255,0.1)' },
  btnSecondary: {
    background: 'rgba(255,255,255,0.06)',
    color: 'rgba(255,255,255,0.85)',
    borderColor: 'rgba(255,215,0,0.15)',
  },
  row: { display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 },
  label: {
    display: 'block', fontSize: 10.5, color: 'rgba(255,215,0,0.75)',
    marginBottom: 4, marginTop: 10,
    textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700,
  },
  input: {
    width: '100%', padding: '7px 10px', borderRadius: 8,
    border: '1px solid rgba(255,215,0,0.18)',
    background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.92)',
    fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit',
    outline: 'none',
  },
  numInput: {
    width: 80, padding: '6px 8px', borderRadius: 8,
    border: '1px solid rgba(255,215,0,0.18)',
    background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.92)',
    fontSize: 13, fontFamily: 'inherit', outline: 'none',
  },
  colorInput: {
    width: 36, height: 28, padding: 0,
    border: '1px solid rgba(255,215,0,0.25)',
    background: 'transparent', borderRadius: 6, cursor: 'pointer',
  },
  list: { margin: '8px 0', padding: 0, listStyle: 'none' },
  listItem: {
    padding: '8px 10px', borderRadius: 10, cursor: 'pointer',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 4, background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,215,0,0.08)',
    transition: 'background 0.15s, border-color 0.15s',
  },
  listItemSelected: {
    background: 'rgba(255,215,0,0.12)',
    borderColor: 'rgba(255,215,0,0.5)',
  },
  separator: { borderTop: '1px solid rgba(255,215,0,0.15)', margin: '12px 0' },
  langTabs: { display: 'flex', gap: 4, marginBottom: 6 },
  langTab: {
    padding: '4px 10px', borderRadius: 6,
    border: '1px solid rgba(255,215,0,0.2)',
    background: 'transparent', color: 'rgba(255,255,255,0.6)',
    fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
  },
  langTabActive: {
    background: 'rgba(255,215,0,0.95)', color: '#120601', borderColor: 'rgba(255,215,0,0.95)',
  },
  addModeBar: {
    padding: '10px 12px', background: 'rgba(255,215,0,0.12)',
    border: '1px solid rgba(255,215,0,0.4)', borderRadius: 10,
    marginBottom: 10, fontSize: 12, color: 'rgba(255,215,0,0.95)', fontWeight: 600,
  },
  toast: {
    position: 'fixed', bottom: 24, left: '50%',
    transform: 'translateX(-50%)', padding: '10px 20px',
    background: 'rgba(18,6,1,0.97)', color: 'rgba(255,215,0,0.95)',
    borderRadius: 999, fontSize: 13, fontWeight: 700,
    zIndex: Z_INDEX.NAV_HINTS_EDITOR + 1,
    border: '1px solid rgba(255,215,0,0.4)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
    fontFamily: "'Noto Sans Devanagari', system-ui, sans-serif",
  },
  captureOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: Z_INDEX.NAV_HINTS_EDITOR - 1,
    cursor: 'crosshair',
    background: 'rgba(255,215,0,0.04)',
  },
};

// Enabled only when master edit mode is active (Alt+Shift+E).
const isEnabled = () => true; // legacy flag; real gate is useEditMode() below

// SliderInput — range slider with -/+ stepper buttons. Drop-in replacement
// for `<input type="range" ... />`. Renders [-] [slider] [+] in a flex row;
// buttons nudge by `step` and clamp to [min, max]. Useful when the native
// slider gets stuck in pointer drag and the user needs precise control.
const SliderInput = ({ min, max, step, value, onChange, style, disabled }) => {
  const minN = parseFloat(min);
  const maxN = parseFloat(max);
  const stepN = parseFloat(step) || 1;
  const valN = parseFloat(value);
  const stepBy = (delta) => {
    if (Number.isNaN(valN)) return;
    let next = valN + delta * stepN;
    next = Math.round(next / stepN) * stepN;
    if (!Number.isNaN(minN)) next = Math.max(minN, next);
    if (!Number.isNaN(maxN)) next = Math.min(maxN, next);
    const decimals = (String(stepN).split('.')[1] || '').length;
    if (decimals) next = parseFloat(next.toFixed(decimals));
    onChange?.({ target: { value: String(next) } });
  };
  const btnStyle = {
    padding: 0, width: 24, height: 24, borderRadius: 6,
    border: '1px solid rgba(255,215,0,0.25)',
    background: 'rgba(255,255,255,0.06)',
    color: 'rgba(255,215,0,0.9)',
    fontSize: 14, fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit', flexShrink: 0, lineHeight: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    userSelect: 'none',
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, ...style }}>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => stepBy(-1)}
        style={btnStyle}
        disabled={disabled}
        title="Decrease"
        aria-label="Decrease"
      >−</button>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={onChange}
        disabled={disabled}
        style={{ flex: 1, minWidth: 0 }}
      />
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => stepBy(1)}
        style={btnStyle}
        disabled={disabled}
        title="Increase"
        aria-label="Increase"
      >+</button>
    </div>
  );
};

const NavHintsEditor = ({ active = true }) => {
  const [enabled] = useState(isEnabled);
  const editMode = useEditMode();
  const [open, setOpen] = useState(false);
  const [hints, setHints] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [editLang, setEditLang] = useState('en');
  const [addMode, setAddMode] = useState(false);
  // Which type will the next placed hint be? 'nav' or 'poi'.
  const [pendingType, setPendingType] = useState('poi');
  // When set, the next click on the map MOVES this hint instead of
  // placing a new one. Cleared after a successful move or cancel.
  const [moveMode, setMoveMode] = useState(false);
  const [toast, setToast] = useState('');
  // Global settings panel: which type we're editing ('poi'|'pill') and
  // whether the panel is expanded. `globalRev` bumps on every update so
  // the section re-renders without holding a full style copy in state.
  const [globalType, setGlobalType] = useState('poi');
  const [globalOpen, setGlobalOpen] = useState(false);
  const [globalRev, setGlobalRev] = useState(0);
  // ID of the hint currently in "confirm delete" mode in the list. Clicking
  // ✕ once arms it; a second confirmation click within the same arming
  // session actually removes the hint. Prevents accidental deletions.
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  // Manifest of all images on disk under public/assets/images/pois/, used
  // by the "Add from POI library" picker in the gallery section.
  const [poiManifest, setPoiManifest] = useState(null);
  const iframeRef = useRef(null);

  // Lazy-load the POI image manifest the first time the editor opens.
  useEffect(() => {
    if (!open || poiManifest !== null) return;
    fetch('/config/poiImageManifest.json?t=' + Date.now())
      .then((r) => r.ok ? r.json() : null)
      .then((j) => setPoiManifest(j?.folders || {}))
      .catch(() => setPoiManifest({}));
  }, [open, poiManifest]);

  const refresh = useCallback(() => {
    setHints(navHintsService.getAll());
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    return navHintsService.subscribe(refresh);
  }, [enabled, refresh]);

  useEffect(() => {
    iframeRef.current = document.querySelector('iframe[title="Matterport"]')
      || document.querySelector('iframe');
  }, [open]);

  // Publish editor-active state so the renderer can draw dev-only overlays
  // (radius rings). Reset on unmount so overlays never leak into prod view.
  useEffect(() => {
    navHintsService.setEditorActive(open);
    return () => navHintsService.setEditorActive(false);
  }, [open]);

  // Mirror the currently-edited hint id so the renderer only shows the
  // radius ring for the hint under active edit.
  useEffect(() => {
    navHintsService.setEditorSelectedId(open ? selectedId : null);
    return () => navHintsService.setEditorSelectedId(null);
  }, [open, selectedId]);

  const showToast = useCallback((msg, ms = 1600) => {
    setToast(msg);
    setTimeout(() => setToast(''), ms);
  }, []);

  const selected = hints.find((h) => h.id === selectedId) || null;

  // Click-to-place handler — invoked by the fullscreen capture overlay below
  // (so the Matterport iframe never receives the click and the camera stays put).
  const handleOverlayClick = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const sdk = matterportService.state?.sdk;
    if (!sdk || !sdk.Renderer || !sdk.Renderer.getWorldPositionData) {
      showToast('Renderer intersection unavailable');
      setAddMode(false);
      setMoveMode(false);
      return;
    }
    try {
      const iframe = iframeRef.current
        || document.querySelector('iframe[title="Matterport"]')
        || document.querySelector('iframe');
      const rect = iframe?.getBoundingClientRect();
      if (!rect) return;
      const posData = await sdk.Renderer.getWorldPositionData({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      if (!posData?.position) { showToast('No surface hit'); return; }
      const { x, y, z } = posData.position;

      // ── Move-existing branch ───────────────────────────────────
      // When move-mode is active, reposition the currently selected
      // hint instead of placing a new one. Direction signs always
      // snap their Y to the raycast hit (the floor) so the post base
      // never floats above or sinks below the ground.
      if (moveMode && selected) {
        const isDir = selected.type === 'directionSign';
        const isGA  = selected.type === 'groundArrow';
        const newPos = (isDir || isGA)
          ? {
              x: parseFloat(x.toFixed(3)),
              y: parseFloat(y.toFixed(3)), // snap to floor
              z: parseFloat(z.toFixed(3)),
            }
          : {
              x: parseFloat(x.toFixed(3)),
              // Preserve existing Y for floating markers (POI/pill/billboard)
              // so they don't drop to the floor on move.
              y: selected.position?.y ?? parseFloat(y.toFixed(3)),
              z: parseFloat(z.toFixed(3)),
            };
        navHintsService.update(selected.id, { position: newPos });
        setMoveMode(false);
        showToast((isDir || isGA) ? 'Moved (floor-snapped)' : 'Position updated');
        return;
      }

      const isPoi = pendingType === 'poi';
      const isPill = pendingType === 'pill';
      const isBillboard = pendingType === 'billboard';
      const isDirectionSign = pendingType === 'directionSign';
      const isGroundArrow = pendingType === 'groundArrow';
      // Nav hints capture an approach vector so they only show from the
      // authored side. POI markers + pills + billboards + direction signs
      // + ground arrows are omnidirectional.
      let approachVector = null;
      if (!isPoi && !isPill && !isBillboard && !isDirectionSign && !isGroundArrow) {
        const pose = matterportService.getCurrentPose();
        if (pose && pose.position) {
          const dx = x - pose.position.x;
          const dz = z - pose.position.z;
          const len = Math.sqrt(dx * dx + dz * dz);
          if (len > 0.01) {
            approachVector = {
              x: parseFloat((dx / len).toFixed(3)),
              z: parseFloat((dz / len).toFixed(3)),
            };
          }
        }
      }
      const labelsByType = isPoi
        ? { mr: 'नवीन स्थळ', en: 'New POI', hi: 'नया स्थान' }
        : isPill
          ? { mr: 'गंतव्य', en: 'Destination', hi: 'गंतव्य' }
          : isBillboard
            ? { mr: 'सूचना फलक', en: 'Information', hi: 'सूचना' }
            : isDirectionSign
              ? { mr: 'दिशा फलक', en: 'Direction Sign', hi: 'दिशा संकेत' }
              : isGroundArrow
                ? { mr: '', en: '', hi: '' }
                : { mr: 'नवीन संकेत', en: 'New hint', hi: 'नया संकेत' };
      // Default the billboard's facing direction to the camera's current
      // yaw — feels natural at placement time. Author can rotate later.
      let billboardYaw = 0;
      if (isBillboard || isDirectionSign) {
        const pose = matterportService.getCurrentPose();
        if (pose?.rotation?.y != null) {
          // Make the board face the camera (180° from camera yaw).
          let y = pose.rotation.y + 180;
          y = ((y % 360) + 360) % 360;
          billboardYaw = parseFloat(y.toFixed(2));
        }
      }
      const typeDefaults = isPoi ? {
        // POI markers are text-only; icon glyph is suppressed at render
        // time regardless of these fields.
        maxDistance: 8,
        // POIs sit flush with their anchor point (0m vertical offset).
        offsetY: 0,
        approachVector: null,
      } : isPill ? {
        // Pill visuals are now owned by the global `pill` style block in
        // navHintsService. Only behaviour defaults live here.
        maxDistance: 8,
        // Pills float 1m above the floor by default.
        offsetY: 1,
        approachVector: null,
      } : isBillboard ? {
        // Billboards are full 3D — no DOM overlay logic, so distance/
        // approach gates are irrelevant. Just record the world-anchored
        // yaw + a sensible visual default.
        offsetY: 0,
        rotationY: billboardYaw,
        scale: 1.0,
        boardShape: 'rect',
        baseStand: 'singlePost',
        boardColor: '#FFFFFF',
        boardTextColor: '#111111',
        boardBorderColor: '#111111',
        standColor: '#7d7d7d',
        approachVector: null,
      } : isDirectionSign ? {
        // Direction signposts are full 3D and world-anchored — the post
        // sits flush with the floor and each arm has its own world-space
        // yaw. Start with two arms pointing left/right so the new sign is
        // immediately visible after placement.
        offsetY: 0,
        rotationY: billboardYaw,
        scale: 1.0,
        signVariant: 'A',
        signArms: [
          {
            labels: { mr: 'मुख्य मंदिर', en: 'Main Mandir', hi: 'मुख्य मंदिर' },
            yaw: billboardYaw,
          },
          {
            labels: { mr: 'प्रवेश', en: 'Entrance', hi: 'प्रवेश' },
            yaw: (billboardYaw + 180) % 360,
          },
        ],
        approachVector: null,
      } : isGroundArrow ? (() => {
        // Ground-painted chevron strip. Auto-orient the arrow so its
        // head points away from the camera through the click point —
        // i.e. lay the strip along the line from where the visitor is
        // standing to the spot they tapped, with the head pointing
        // forward. This always reads as "go that way" instead of a
        // diagonal stripe across the floor.
        //
        // Also capture an `approachVector` from the CAMERA'S CURRENT
        // FACING DIRECTION (not the camera→click vector). This means
        // the arrow is only visible when a viewer approaches from the
        // same direction the author was facing — so walking back along
        // the same path (looking the opposite way) hides it. The user
        // can still override via the editor's "Capture approach" tool.
        const pose = matterportService.getCurrentPose();
        let yaw = 0;
        let groundArrowApproach = null;
        if (pose?.rotation?.y != null) {
          yaw = ((pose.rotation.y % 360) + 360) % 360;
          // Matterport convention: forward = (sin yaw, -cos yaw).
          // approachVector is the unit camera→hint floor vector, which
          // for someone walking forward equals the camera facing dir.
          const r = (yaw * Math.PI) / 180;
          groundArrowApproach = {
            x: parseFloat(Math.sin(r).toFixed(3)),
            z: parseFloat((-Math.cos(r)).toFixed(3)),
          };
        } else if (pose?.position) {
          // Fallback when rotation is unavailable: use camera→click.
          const dxA = x - pose.position.x;
          const dzA = z - pose.position.z;
          const lenA = Math.sqrt(dxA * dxA + dzA * dzA);
          if (lenA > 0.05) {
            yaw = (Math.atan2(dxA, -dzA) * 180) / Math.PI;
            yaw = ((yaw % 360) + 360) % 360;
            groundArrowApproach = {
              x: parseFloat((dxA / lenA).toFixed(3)),
              z: parseFloat((dzA / lenA).toFixed(3)),
            };
          }
        }
        return {
          offsetY: 0.02,
          forwardYaw: parseFloat(yaw.toFixed(2)),
          arrowLength: 2.5,
          arrowWidth: 0.9,
          highlightRadius: 3,
          arrowColor: '#f59e0b',
          arrowVariant: 'chevrons',
          arrowTailOpacity: 0.05,
          labelHeight: 0.6,
          labelFontSize: 16,
          maxDistance: 12,
          fadeStart: 9,
          approachVector: groundArrowApproach,
          // Stricter than the 0.3 default: only show within a ~120°
          // cone in front of the authored direction so the arrow
          // disappears as soon as the viewer turns around.
          approachThreshold: 0.5,
        };
      })() : {
        offsetY: 1,
        approachVector,
      };
      const id = navHintsService.add({
        type: pendingType,
        position: {
          x: parseFloat(x.toFixed(3)),
          y: parseFloat(y.toFixed(3)),
          z: parseFloat(z.toFixed(3)),
        },
        labels: labelsByType,
        ...typeDefaults,
      });
      setSelectedId(id);
      setAddMode(false);
      showToast(
        isPoi ? 'POI placed'
        : isPill ? 'Pill placed — pick a target'
        : isBillboard ? 'Billboard placed'
        : isDirectionSign ? 'Direction sign placed'
        : isGroundArrow ? 'Ground arrow placed'
        : 'Hint placed'
      );
    } catch (err) {
      console.warn('NavHintsEditor: click-to-place failed', err);
      showToast('Placement failed');
    }
  }, [showToast, pendingType, moveMode, selected]);

  const updateSelected = useCallback((patch) => {
    if (!selected) return;
    navHintsService.update(selected.id, patch);
  }, [selected]);

  // Capture the current camera→hint floor-plane direction. The hint will
  // then only be visible when a viewer stands in a similar relative spot.
  const captureApproachDirection = useCallback(() => {
    if (!selected) return;
    const pose = matterportService.getCurrentPose();
    if (!pose || !pose.position) { showToast('No camera pose yet'); return; }
    const dx = selected.position.x - pose.position.x;
    const dz = selected.position.z - pose.position.z;
    const len = Math.sqrt(dx * dx + dz * dz);
    if (len < 0.01) { showToast('Stand farther from the hint'); return; }
    updateSelected({
      approachVector: {
        x: parseFloat((dx / len).toFixed(3)),
        z: parseFloat((dz / len).toFixed(3)),
      },
    });
    showToast('Approach direction captured');
  }, [selected, updateSelected, showToast]);

  const clearApproachDirection = useCallback(() => {
    updateSelected({ approachVector: null });
    showToast('Now visible from all directions');
  }, [updateSelected, showToast]);

  const copyJSON = useCallback(async () => {
    try {
      const json = JSON.stringify(navHintsService.toJSON(), null, 2);
      await navigator.clipboard.writeText(json);
      showToast('JSON copied to clipboard');
    } catch (err) {
      console.warn(err);
      showToast('Copy failed — see console');
      // eslint-disable-next-line no-console
      console.log('[NavHintsEditor] JSON export:\n', JSON.stringify(navHintsService.toJSON(), null, 2));
    }
  }, [showToast]);

  /**
   * "Save to project" — persist the current hints to
   * public/config/navHintsConfig.json so they load automatically next time.
   *
   * Order of attempts:
   *   1. POST to /__dev/saveNavHints (handled by src/setupProxy.js when
   *      running `npm start`). Silent & automatic — no dialogs.
   *   2. File System Access API (Chromium) — user picks file once.
   *   3. Download fallback — user drops file into public/config/.
   */
  const fileHandleRef = useRef(null);
  const saveToProject = useCallback(async () => {
    const json = JSON.stringify(navHintsService.toJSON(), null, 2);

    // 1. Dev-server endpoint (automatic)
    try {
      const res = await fetch('/__dev/saveNavHints', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: json,
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        showToast(`Saved ${data.count ?? ''} hint(s) to project ✓`);
        return;
      }
    } catch (_) {
      // endpoint not available (prod build) — fall through
    }

    // 2. File System Access API (Chromium-only, production/static hosting)
    if (window.showSaveFilePicker) {
      try {
        if (!fileHandleRef.current) {
          fileHandleRef.current = await window.showSaveFilePicker({
            suggestedName: 'navHintsConfig.json',
            types: [{
              description: 'JSON',
              accept: { 'application/json': ['.json'] },
            }],
          });
        }
        const writable = await fileHandleRef.current.createWritable();
        await writable.write(json);
        await writable.close();
        showToast('Saved to selected file');
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return;
        console.warn('FS Access API failed, falling back to download:', err);
      }
    }

    // 3. Download fallback
    try {
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'navHintsConfig.json';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      showToast('Downloaded — move to public/config/');
    } catch (err) {
      console.warn(err);
      showToast('Save failed — see console');
    }
  }, [showToast]);

  const reloadFromDisk = useCallback(async () => {
    const ok = await navHintsService.loadFromConfig();
    showToast(ok ? 'Reloaded from project file' : 'No config file found');
  }, [showToast]);

  if (!enabled || !active || !editMode) return null;

  if (!open) {
    return (
      <button
        type="button"
        style={styles.toggleBtn}
        onClick={() => setOpen(true)}
        title="POI Editor"
      >
        <span style={{ fontSize: 16 }}>📍</span>
        <span>POI Editor</span>
      </button>
    );
  }

  return (
    <>
      {/* Scoped styling for <select> dropdowns inside this panel — native
          <option> elements inherit OS theme, which produced unreadable
          white-on-white text. Forcing an opaque dark bg + light fg on
          options restores contrast across Chromium & Firefox. */}
      <style>{`
        .nav-hints-editor select { color-scheme: dark; }
        .nav-hints-editor select option {
          background-color: #120601;
          color: #f5f5f5;
        }
        .nav-hints-editor select option:checked {
          background-color: #2a1d04;
          color: #ffd24d;
        }
      `}</style>
      <div className="nav-hints-editor" style={styles.panel}>
        <div style={styles.header}>
          <h3 style={styles.title}>POI Editor</h3>
          <button type="button" style={styles.closeBtn} onClick={() => setOpen(false)}>×</button>
        </div>

        {addMode && (
          <div style={styles.addModeBar}>
            {pendingType === 'pill'
              ? 'Click in the scene to place a wayfinding pill.'
              : pendingType === 'billboard'
                ? 'Click on the ground to place a billboard signage.'
                : pendingType === 'directionSign'
                  ? 'Click on the ground to place a direction signpost.'
                  : pendingType === 'groundArrow'
                    ? 'Click on the ground — chevron strip aligns to camera-forward.'
                    : 'Click in the scene to place a POI marker.'}
            <button
              type="button"
              style={{ ...styles.btn, ...styles.btnSecondary, marginLeft: 8, padding: '2px 8px' }}
              onClick={() => setAddMode(false)}
            >Cancel</button>
          </div>
        )}

        {moveMode && selected && (
          <div style={{
            ...styles.addModeBar,
            background: 'rgba(120,180,255,0.12)',
            border: '1px solid rgba(120,180,255,0.35)',
            color: 'rgba(180,210,255,0.95)',
          }}>
            {selected.type === 'directionSign'
              ? 'Click on the ground — direction sign will snap to floor.'
              : 'Click anywhere in the scene to move this hint.'}
            <button
              type="button"
              style={{ ...styles.btn, ...styles.btnSecondary, marginLeft: 8, padding: '2px 8px' }}
              onClick={() => setMoveMode(false)}
            >Cancel</button>
          </div>
        )}

        {/* "+ add" buttons — POI marker, Nav pill, Direction signpost,
            Ground-arrow chevron strip, and 3D world-anchored label.
            Type is chosen here; once placed, a hint's type is fixed
            (delete & re-add to change it). */}
        <div style={{ ...styles.row, flexWrap: 'wrap' }}>
          {[
            { value: 'poi', label: '+ POI', primary: true, title: 'Place an omnidirectional clickable POI marker' },
            { value: 'groundArrow', label: '+ Ground arrow', primary: false, title: 'Paint a chevron strip on the floor with an inline label — pulses when the visitor is near' },
          ].map(({ value, label, primary, title }) => {
            const isActive = addMode && pendingType === value;
            return (
              <button
                key={value}
                type="button"
                style={{
                  ...styles.btn,
                  ...(isActive
                    ? styles.btnDanger
                    : primary
                      ? styles.btnPrimary
                      : styles.btnSecondary),
                  flex: '1 1 30%',
                  padding: '8px 4px',
                  fontSize: 11,
                  whiteSpace: 'nowrap',
                }}
                onClick={() => {
                  setPendingType(value);
                  setMoveMode(false);
                  setAddMode((v) => !(v && pendingType === value));
                }}
                title={title}
              >{isActive ? 'Cancel' : label}</button>
            );
          })}
        </div>
        <div style={{ ...styles.row, marginTop: 6 }}>
          <button
            type="button"
            style={{ ...styles.btn, ...styles.btnPrimary, flex: 1 }}
            onClick={saveToProject}
            title="Save hints to public/config/navHintsConfig.json"
          >💾 Save to project</button>
          <button type="button" style={{ ...styles.btn, ...styles.btnSecondary }} onClick={reloadFromDisk} title="Reload from project file">
            ↻
          </button>
          <button type="button" style={{ ...styles.btn, ...styles.btnSecondary }} onClick={copyJSON} title="Copy JSON to clipboard">
            📋
          </button>
        </div>

        <div style={styles.separator} />

        {/* ── Global settings ─────────────────────────────────────────
            All visual knobs (shape, colors, font weight, bg opacity,
            vertical offset, pill arrow width) are managed here per-type
            rather than per-hint — a single change applies to every POI
            or pill in the tour. Collapsed by default to keep the panel
            compact. */}
        <div style={{
          marginBottom: 8, padding: '8px 10px',
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,215,0,0.15)',
          borderRadius: 6,
        }}>
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setGlobalOpen((v) => {
              const next = !v;
              if (next) {
                // Auto-switch to the type the author is currently
                // working with: prefer the selected hint, else the
                // pending add-type, else keep the current tab.
                const ctx = (selected?.type === 'groundArrow' || selected?.type === 'poi')
                  ? selected.type
                  : (pendingType === 'groundArrow' || pendingType === 'poi')
                    ? pendingType
                    : null;
                if (ctx) setGlobalType(ctx);
              }
              return next;
            })}
          >
            <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,215,0,0.95)', flex: 1, letterSpacing: '0.03em' }}>
              ⚙ GLOBAL SETTINGS
            </span>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>{globalOpen ? '▾' : '▸'}</span>
          </div>
          {globalOpen && (() => {
            const gs = navHintsService.getGlobalStyle(globalType);
            void globalRev; // keeps this block dependent on re-renders after patches
            const patchGlobal = (patch) => {
              navHintsService.updateGlobalStyle(globalType, patch);
              setGlobalRev((r) => r + 1);
            };
            return (
              <div style={{ marginTop: 8 }}>
                {/* Type tabs */}
                <div style={{ ...styles.row, marginBottom: 8 }}>
                  {['poi', 'groundArrow'].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setGlobalType(t)}
                      style={{
                        ...styles.btn,
                        ...(globalType === t ? styles.btnPrimary : styles.btnSecondary),
                        flex: 1, padding: '6px 8px', fontSize: 11,
                      }}
                    >{
                      t === 'poi' ? 'POI markers'
                      : t === 'pill' ? 'Nav pills'
                      : t === 'groundArrow' ? 'Ground arrow'
                      : 'Direction sign'
                    }</button>
                  ))}
                </div>

                {globalType === 'groundArrow' ? (
                  <>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginBottom: 6 }}>
                      Visual style applied to <em>every</em> ground arrow.
                      Per-hint values like heading, length, turn, and label
                      stay on each arrow.
                    </div>

                    <label style={styles.label}>Variant</label>
                    <select
                      style={styles.input}
                      value={gs.arrowVariant || 'chevrons'}
                      onChange={(e) => patchGlobal({ arrowVariant: e.target.value })}
                    >
                      <option value="chevrons">Chevrons (classic)</option>
                      <option value="triangles">Triangles (solid)</option>
                      <option value="doubleChevron">Double chevrons (bold)</option>
                      <option value="dashes">Dashes (minimal)</option>
                      <option value="beam">Beam (single arrow)</option>
                    </select>

                    <label style={styles.label}>
                      Number of breaks (chevrons): {(gs.chevronCount ?? 0) > 0 ? gs.chevronCount : 'auto'}
                    </label>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                      How many segments make up the strip. <strong>0 = auto</strong>
                      (uses the variant's natural count). Slide right to add
                      more breaks. Ignored by the Beam variant (always 1).
                    </div>
                    <div style={styles.row}>
                      <SliderInput
                        type="range"
                        min="0" max="20" step="1"
                        value={gs.chevronCount ?? 0}
                        onChange={(e) => patchGlobal({ chevronCount: parseInt(e.target.value, 10) })}
                        style={{ flex: 1 }}
                      />
                      <input
                        style={{ ...styles.numInput, width: 60 }}
                        type="number" min="0" max="30" step="1"
                        value={gs.chevronCount ?? 0}
                        onChange={(e) => patchGlobal({ chevronCount: parseInt(e.target.value, 10) || 0 })}
                      />
                    </div>

                    <label style={styles.label}>Tint colour</label>
                    <div style={styles.row}>
                      <input
                        style={styles.colorInput}
                        type="color"
                        value={gs.arrowColor || '#f59e0b'}
                        onChange={(e) => patchGlobal({ arrowColor: e.target.value })}
                      />
                      <input
                        style={{ ...styles.input, flex: 1 }}
                        type="text"
                        value={gs.arrowColor || ''}
                        onChange={(e) => patchGlobal({ arrowColor: e.target.value })}
                      />
                    </div>

                    <label style={styles.label}>Width: {(gs.arrowWidth ?? 0.9).toFixed(2)} m</label>
                    <SliderInput
                      type="range"
                      min="0.3" max="2.5" step="0.05"
                      value={gs.arrowWidth ?? 0.9}
                      onChange={(e) => patchGlobal({ arrowWidth: parseFloat(e.target.value) })}
                      style={{ width: '100%' }}
                    />

                    <label style={styles.label}>
                      Tail opacity: {Math.round((gs.arrowTailOpacity ?? 0.05) * 100)}%
                    </label>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                      How transparent the tail end is. The head is always 100%.
                    </div>
                    <SliderInput
                      type="range"
                      min="0" max="1" step="0.05"
                      value={gs.arrowTailOpacity ?? 0.05}
                      onChange={(e) => patchGlobal({ arrowTailOpacity: parseFloat(e.target.value) })}
                      style={{ width: '100%' }}
                    />

                    <label style={styles.label}>Highlight radius: {(gs.highlightRadius ?? 3).toFixed(1)} m</label>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                      Within this distance, chevrons brighten to grab attention.
                    </div>
                    <SliderInput
                      type="range"
                      min="0.5" max="10" step="0.25"
                      value={gs.highlightRadius ?? 3}
                      onChange={(e) => patchGlobal({ highlightRadius: parseFloat(e.target.value) })}
                      style={{ width: '100%' }}
                    />

                    <label style={styles.label}>Label font size: {(gs.labelFontSize ?? 16)} px</label>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                      Base font size of the floating label. Combined with
                      "Label world scale" below to determine the on-screen
                      size as the camera moves.
                    </div>
                    <div style={styles.row}>
                      <SliderInput
                        type="range"
                        min="8" max="96" step="1"
                        value={gs.labelFontSize ?? 16}
                        onChange={(e) => patchGlobal({ labelFontSize: parseInt(e.target.value, 10) })}
                        style={{ flex: 1 }}
                      />
                      <input
                        style={{ ...styles.numInput, width: 60 }}
                        type="number" min="8" max="200" step="1"
                        value={gs.labelFontSize ?? 16}
                        onChange={(e) => patchGlobal({ labelFontSize: parseInt(e.target.value, 10) || 16 })}
                      />
                    </div>

                    <label style={styles.label}>
                      Label world scale: {((gs.labelWorldScale ?? 0.01) * 1000).toFixed(0)} mm/px
                    </label>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                      Physical size of the sign in the world. Larger = bigger
                      from far away. The label sits on a fixed vertical plane.
                    </div>
                    <SliderInput
                      type="range"
                      min="0.002" max="0.05" step="0.001"
                      value={gs.labelWorldScale ?? 0.01}
                      onChange={(e) => patchGlobal({ labelWorldScale: parseFloat(e.target.value) })}
                      style={{ width: '100%' }}
                    />

                    <label style={styles.label}>
                      Label vertical position: {(gs.labelHeight ?? 0.6).toFixed(2)} m
                    </label>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                      How high the label floats above the arrow head.
                      0 = on the floor, larger = higher above ground.
                    </div>
                    <div style={styles.row}>
                      <SliderInput
                        type="range"
                        min="0" max="2.5" step="0.05"
                        value={gs.labelHeight ?? 0.6}
                        onChange={(e) => patchGlobal({ labelHeight: parseFloat(e.target.value) })}
                        style={{ flex: 1 }}
                      />
                      <input
                        style={{ ...styles.numInput, width: 60 }}
                        type="number" step="0.05"
                        value={gs.labelHeight ?? 0.6}
                        onChange={(e) => patchGlobal({ labelHeight: parseFloat(e.target.value) || 0 })}
                      />
                    </div>

                    <div style={styles.separator} />
                    <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={gs.arrowFlow !== false}
                        onChange={(e) => patchGlobal({ arrowFlow: e.target.checked })}
                      />
                      <span>Flow animation (tail → head)</span>
                    </label>
                    {(gs.arrowFlow !== false) && (
                      <>
                        <label style={styles.label}>
                          Flow speed: {(gs.arrowFlowSpeed ?? 0.25).toFixed(2)} cyc/s
                        </label>
                        <SliderInput
                          type="range"
                          min="0.05" max="2.5" step="0.05"
                          value={gs.arrowFlowSpeed ?? 0.25}
                          onChange={(e) => patchGlobal({ arrowFlowSpeed: parseFloat(e.target.value) })}
                          style={{ width: '100%' }}
                        />
                        <label style={styles.label}>
                          Pulse width: {Math.round((gs.arrowFlowWidth ?? 0.35) * 100)}%
                        </label>
                        <SliderInput
                          type="range"
                          min="0.05" max="0.6" step="0.01"
                          value={gs.arrowFlowWidth ?? 0.35}
                          onChange={(e) => patchGlobal({ arrowFlowWidth: parseFloat(e.target.value) })}
                          style={{ width: '100%' }}
                        />
                        <label style={styles.label}>
                          Off-pulse floor: {Math.round((gs.arrowFlowFloor ?? 0.8) * 100)}%
                        </label>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                          Minimum chevron opacity outside the moving pulse.
                          Lower = stronger flicker, higher = gentler shimmer.
                        </div>
                        <SliderInput
                          type="range"
                          min="0" max="1" step="0.05"
                          value={gs.arrowFlowFloor ?? 0.8}
                          onChange={(e) => patchGlobal({ arrowFlowFloor: parseFloat(e.target.value) })}
                          style={{ width: '100%' }}
                        />
                      </>
                    )}
                  </>
                ) : globalType === 'directionSign' ? (
                  <>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginBottom: 6 }}>
                      Visibility envelope applied to <em>every</em>
                      direction signpost. Position, arms, colours and
                      yaws stay per-sign.
                    </div>

                    <label style={styles.label}>
                      Max distance: {(gs.maxDistance ?? 25).toFixed(1)} m
                    </label>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                      Past this floor distance, signposts are hidden so
                      they don't clutter the view from across the campus.
                    </div>
                    <div style={styles.row}>
                      <SliderInput
                        type="range"
                        min="2" max="80" step="1"
                        value={gs.maxDistance ?? 25}
                        onChange={(e) => patchGlobal({ maxDistance: parseFloat(e.target.value) })}
                        style={{ flex: 1 }}
                      />
                      <input
                        style={{ ...styles.numInput, width: 60 }}
                        type="number" min="0" step="1"
                        value={gs.maxDistance ?? 25}
                        onChange={(e) => patchGlobal({ maxDistance: parseFloat(e.target.value) || 0 })}
                      />
                    </div>

                    <label style={styles.label}>
                      Fade start: {(gs.fadeStart ?? 0).toFixed(1)} m
                      {(gs.fadeStart ?? 0) === 0 ? ' (no fade)' : ''}
                    </label>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                      When &gt; 0 and &lt; max distance, signs fade in
                      linearly between max distance (0%) and this value
                      (100%). 0 = hard cutoff at max distance.
                    </div>
                    <SliderInput
                      type="range"
                      min="0" max={Math.max(1, gs.maxDistance ?? 25)} step="0.5"
                      value={gs.fadeStart ?? 0}
                      onChange={(e) => patchGlobal({ fadeStart: parseFloat(e.target.value) })}
                      style={{ width: '100%' }}
                    />

                    <label style={styles.label}>
                      Hide when within: {(gs.hideWithin ?? 0).toFixed(2)} m
                      {(gs.hideWithin ?? 0) === 0 ? ' (never hide)' : ''}
                    </label>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                      Hides the signpost when the visitor is closer
                      than this. Prevents the brass cap / band from
                      clipping the near plane and painting thin yellow
                      streaks across the screen at very close range.
                    </div>
                    <SliderInput
                      type="range"
                      min="0" max="3" step="0.05"
                      value={gs.hideWithin ?? 0}
                      onChange={(e) => patchGlobal({ hideWithin: parseFloat(e.target.value) })}
                      style={{ width: '100%' }}
                    />
                  </>
                ) : (
                <>

                <label style={styles.label}>Shape</label>
                <select
                  style={styles.input}
                  value={gs.shape || 'pill'}
                  onChange={(e) => patchGlobal({ shape: e.target.value })}
                >
                  {SHAPE_PRESETS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>

                <label style={styles.label}>Text color</label>
                <div style={styles.row}>
                  <input
                    style={styles.colorInput}
                    type="color"
                    value={gs.textColor || '#ffffff'}
                    onChange={(e) => patchGlobal({ textColor: e.target.value })}
                  />
                  <input
                    style={{ ...styles.input, flex: 1 }}
                    type="text"
                    value={gs.textColor || ''}
                    onChange={(e) => patchGlobal({ textColor: e.target.value })}
                  />
                </div>

                <label style={styles.label}>Background color</label>
                <div style={styles.row}>
                  <input
                    style={styles.colorInput}
                    type="color"
                    value={gs.bgColor || '#000000'}
                    onChange={(e) => patchGlobal({ bgColor: e.target.value })}
                  />
                  <input
                    style={{ ...styles.input, flex: 1 }}
                    type="text"
                    value={gs.bgColor || ''}
                    onChange={(e) => patchGlobal({ bgColor: e.target.value })}
                  />
                </div>

                <label style={styles.label}>Background opacity ({(gs.bgOpacity ?? 0.75).toFixed(2)})</label>
                <SliderInput
                  type="range"
                  min="0" max="1" step="0.05"
                  value={gs.bgOpacity ?? 0.75}
                  onChange={(e) => patchGlobal({ bgOpacity: parseFloat(e.target.value) })}
                  style={{ width: '100%' }}
                />

                <label style={styles.label}>Font weight</label>
                <select
                  style={styles.input}
                  value={gs.fontWeight || 700}
                  onChange={(e) => patchGlobal({ fontWeight: parseInt(e.target.value, 10) })}
                >
                  <option value={400}>400 — Normal</option>
                  <option value={600}>600 — Semibold</option>
                  <option value={700}>700 — Bold</option>
                  <option value={800}>800 — Extra Bold</option>
                </select>

                <label style={styles.label}>
                  Font size: {(gs.fontSize || 14)} px
                </label>
                <div style={styles.row}>
                  <SliderInput
                    type="range"
                    min="10" max="32" step="1"
                    value={gs.fontSize || 14}
                    onChange={(e) => patchGlobal({ fontSize: parseInt(e.target.value, 10) })}
                    style={{ flex: 1 }}
                  />
                  <input
                    style={{ ...styles.numInput, width: 60 }}
                    type="number"
                    min="10" max="64" step="1"
                    value={gs.fontSize || 14}
                    onChange={(e) => patchGlobal({ fontSize: parseInt(e.target.value, 10) || 14 })}
                  />
                </div>

                {globalType === 'pill' && (
                  <>
                    <label style={styles.label}>
                      Pill arrow width: {((gs.iconScale ?? 0.85) * 100).toFixed(0)}%
                    </label>
                    <SliderInput
                      type="range"
                      min="0.3" max="1" step="0.05"
                      value={gs.iconScale ?? 0.85}
                      onChange={(e) => patchGlobal({ iconScale: parseFloat(e.target.value) })}
                      style={{ width: '100%' }}
                    />
                  </>
                )}
                </>
                )}
              </div>
            );
          })()}
        </div>

        <div style={{
          fontSize: 11, color: '#999',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>
            {(() => {
              const filtered = hints.filter((h) => h.type === pendingType);
              const total = hints.length;
              const typeName = pendingType === 'directionSign' ? 'direction'
                : pendingType === 'pill' ? 'pill'
                : pendingType === 'poi'  ? 'POI'
                : pendingType;
              return `${filtered.length} ${typeName}${filtered.length === 1 ? '' : 's'} (of ${total})`;
            })()}
          </span>
        </div>
        <ul style={styles.list}>
          {hints.filter((h) => h.type === pendingType).map((h) => (
            <li
              key={h.id}
              style={{
                ...styles.listItem,
                ...(h.id === selectedId ? styles.listItemSelected : {}),
              }}
              onClick={() => {
                setSelectedId(h.id);
                // Cancel any pending delete confirmation that wasn't on
                // this row when the user clicks elsewhere in the list.
                if (pendingDeleteId && pendingDeleteId !== h.id) setPendingDeleteId(null);
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                {(() => {
                  // Colour-code each type so the list is scannable at a glance.
                  const t = h.type || 'nav';
                  const palette = t === 'poi'
                    ? { bg: 'rgba(255,215,0,0.22)', fg: 'rgba(255,215,0,0.95)', br: 'rgba(255,215,0,0.45)' }
                    : t === 'pill'
                      ? { bg: 'rgba(170,255,180,0.18)', fg: 'rgba(180,255,200,0.95)', br: 'rgba(170,255,180,0.45)' }
                      : t === 'billboard'
                        ? { bg: 'rgba(255,170,80,0.18)', fg: 'rgba(255,200,140,0.95)', br: 'rgba(255,170,80,0.45)' }
                        : t === 'directionSign'
                          ? { bg: 'rgba(200,140,255,0.18)', fg: 'rgba(220,180,255,0.95)', br: 'rgba(200,140,255,0.45)' }
                          : { bg: 'rgba(100,180,255,0.18)', fg: 'rgba(140,200,255,0.95)', br: 'rgba(100,180,255,0.35)' };
                  return (
                    <span style={{
                      fontSize: 9, fontWeight: 800, padding: '1px 6px',
                      borderRadius: 4, marginRight: 6, letterSpacing: '0.05em',
                      background: palette.bg, color: palette.fg,
                      border: `1px solid ${palette.br}`,
                    }}>{t.toUpperCase()}</span>
                  );
                })()}
                {(() => {
                  // Editor list always renders names in English so they
                  // stay stable as the author switches edit-language tabs
                  // (and independent of the app-wide UI language).
                  if (h.type === 'pill' && h.targetSid) {
                    const poi = hints.find((x) => x.id === h.targetSid);
                    if (poi) {
                      return poi.labels?.en
                        || poi.labels?.mr
                        || poi.labels?.hi
                        || '(unlabeled)';
                    }
                  }
                  // Direction signposts: show the arm destination names
                  // (per-arm label, falling back to the linked target POI)
                  // instead of the generic stored "Direction Sign" label.
                  if (h.type === 'directionSign' && Array.isArray(h.signArms) && h.signArms.length) {
                    const armNames = h.signArms.map((arm) => {
                      const own = (arm.labels?.en || '').trim();
                      if (own && own !== 'New destination') return own;
                      if (arm.targetId) {
                        const tgt = hints.find((x) => x.id === arm.targetId);
                        const tgtName = (tgt?.labels?.en || '').trim();
                        if (tgtName) return tgtName;
                      }
                      return own || '';
                    }).filter(Boolean);
                    if (armNames.length) return armNames.join(' • ');
                  }
                  // Ground arrows: when a Target POI is linked and no
                  // custom-label override is set, mirror the linked
                  // POI's English name (matches the on-map label).
                  if (h.type === 'groundArrow' && h.targetId && h.customLabels !== true) {
                    const tgt = hints.find((x) => x.id === h.targetId && x.type === 'poi');
                    const tgtName =
                      tgt?.labels?.en
                      || tgt?.labels?.mr
                      || tgt?.labels?.hi;
                    if (tgtName) return tgtName;
                  }
                  return h.labels?.en || '(unlabeled)';
                })()}
              </span>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
                <button
                  type="button"
                  title="Fly camera to this hint"
                  style={{ ...styles.btn, ...styles.btnSecondary, padding: '2px 6px', fontSize: 11 }}
                  onClick={async (e) => {
                    e.stopPropagation();
                    setSelectedId(h.id);
                    const p = h.position || {};
                    const targetY = (p.y || 0) + (h.offsetY || 0);
                    const sdk = matterportService.state?.sdk;
                    if (!sdk?.Sweep?.moveTo || !sdk?.Sweep?.data) {
                      // Fallback when SDK helpers aren't available.
                      matterportService.navigateToPosition(p.x || 0, p.y || 0, p.z || 0);
                      return;
                    }
                    try {
                      // Find the sweep closest to the hint and aim the
                      // camera at the POI from THAT sweep — not from the
                      // viewer's current pose. Reading the current pose
                      // immediately after Sweep.moveTo is racy because
                      // the FLY transition is still in progress when the
                      // promise resolves on some bundle versions.
                      const sweepData = await sdk.Sweep.data.waitUntil(
                        (c) => Object.keys(c).length > 0,
                      );
                      let closest = null;
                      let minDist = Infinity;
                      for (const id of Object.keys(sweepData)) {
                        const s = sweepData[id];
                        if (!s?.position) continue;
                        const d = Math.hypot(
                          s.position.x - (p.x || 0),
                          s.position.z - (p.z || 0),
                        );
                        if (d < minDist) { minDist = d; closest = s; }
                      }
                      if (!closest) return;
                      const dx = (p.x || 0) - closest.position.x;
                      const dz = (p.z || 0) - closest.position.z;
                      const dy = targetY - closest.position.y;
                      const horiz = Math.hypot(dx, dz);
                      // Matterport yaw: 0 faces -Z, CW from above →
                      // forward = (sin yaw, -cos yaw) → yaw = atan2(dx,-dz).
                      // Pitch: positive = look up.
                      const yawDeg = Math.atan2(dx, -dz) * 180 / Math.PI;
                      const pitchDeg = Math.atan2(
                        dy,
                        Math.max(0.001, horiz),
                      ) * 180 / Math.PI;
                      await sdk.Sweep.moveTo(closest.id, {
                        rotation: { x: pitchDeg, y: yawDeg },
                        transition: sdk.Sweep.Transition.FLY,
                        transitionTime: 1000,
                      });
                      // Force-apply rotation — if we were already on the
                      // target sweep Sweep.moveTo is a no-op and the
                      // rotation argument is ignored.
                      await sdk.Camera?.setRotation?.(
                        { x: pitchDeg, y: yawDeg },
                        { speed: 100 },
                      ).catch(() => {});
                    } catch (err) {
                      console.warn('⚠️ Fly-to failed:', err?.message || err);
                    }
                  }}
                >🛬</button>
                {pendingDeleteId === h.id ? (
                  <>
                    <button
                      type="button"
                      title="Confirm delete"
                      style={{ ...styles.btn, ...styles.btnDanger, padding: '2px 6px', fontSize: 10, fontWeight: 700 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navHintsService.remove(h.id);
                        if (selectedId === h.id) setSelectedId(null);
                        setPendingDeleteId(null);
                      }}
                    >Yes</button>
                    <button
                      type="button"
                      title="Cancel delete"
                      style={{ ...styles.btn, ...styles.btnSecondary, padding: '2px 6px', fontSize: 10 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPendingDeleteId(null);
                      }}
                    >No</button>
                  </>
                ) : (
                  <button
                    type="button"
                    title="Delete (asks for confirmation)"
                    style={{ ...styles.btn, ...styles.btnDanger, padding: '2px 6px', fontSize: 10 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPendingDeleteId(h.id);
                    }}
                  >✕</button>
                )}
              </div>
            </li>
          ))}
          {hints.length === 0 && (
            <li style={{ fontSize: 11, color: '#666', padding: 6 }}>
              No hints yet. Use “+ POI marker” or “+ Nav pill”, then click in the scene.
            </li>
          )}
        </ul>

        {selected && (
          <>
            <div style={styles.separator} />
            <div style={{
              fontSize: 10.5, color: 'rgba(255,215,0,0.7)', marginBottom: 8,
              padding: '6px 10px', background: 'rgba(255,215,0,0.06)',
              border: '1px solid rgba(255,215,0,0.15)', borderRadius: 6,
            }}>
              ✓ Live edits · Click 💾 Save to persist
            </div>
            <div style={styles.langTabs}>
              {LANGUAGES.map((l) => (
                <button
                  key={l}
                  type="button"
                  style={{ ...styles.langTab, ...(editLang === l ? styles.langTabActive : {}) }}
                  onClick={() => setEditLang(l)}
                >{LANG_LABELS[l]}</button>
              ))}
            </div>

            <label style={styles.label}>Type</label>
            <div style={{
              fontSize: 12, color: 'rgba(255,255,255,0.8)', marginBottom: 6,
              padding: '6px 10px', background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,215,0,0.12)', borderRadius: 6,
            }}>
              {selected.type === 'poi' ? '📍 POI marker'
                : selected.type === 'pill' ? '💊 Nav pill'
                : selected.type === 'billboard' ? '🪧 Billboard signage'
                : selected.type === 'directionSign' ? '🪧 Direction signpost'
                : selected.type === 'groundArrow' ? '➤ Ground arrow'
                : '🧭 Nav arrow'}
              <span style={{ color: 'rgba(255,255,255,0.4)', marginLeft: 6, fontSize: 10 }}>
                (delete & re-add to change)
              </span>
            </div>

            {selected.type === 'pill' && (
              <>
                <label style={styles.label}>Target POI</label>
                <select
                  style={styles.input}
                  value={selected.targetSid || ''}
                  onChange={(e) => updateSelected({ targetSid: e.target.value })}
                >
                  <option value="">— none (static pill) —</option>
                  {hints
                    .filter((h) => h.type === 'poi')
                    .map((h) => {
                      // Editor dropdowns always show English names.
                      const name =
                        h.labels?.en
                        || h.labels?.mr
                        || h.labels?.hi
                        || `POI ${h.id}`;
                      return (
                        <option key={h.id} value={h.id}>{name}</option>
                      );
                    })}
                </select>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>
                  Place POI markers first, then pick one here. The pill's
                  destination name is read from this POI; the arrow & label
                  below are author-defined (see Direction).
                </div>

                {/* ── Direction (world-anchored) ─────────────────── */}
                <label style={styles.label}>
                  Forward yaw:{' '}
                  {selected.forwardYaw != null
                    ? `${Math.round(selected.forwardYaw)}°`
                    : 'auto (legacy: rotates toward target POI)'}
                </label>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>
                  World-space direction the pill's arrow points to. As you
                  rotate the camera the arrow swings + the label re-buckets
                  to keep pointing the same real-world spot.
                </div>
                <div style={styles.row}>
                  <SliderInput
                    type="range"
                    min="0" max="360" step="1"
                    value={selected.forwardYaw ?? 0}
                    onChange={(e) => updateSelected({
                      forwardYaw: parseFloat(e.target.value),
                    })}
                    style={{ flex: 1 }}
                  />
                  <input
                    style={{ ...styles.numInput, width: 60 }}
                    type="number"
                    step="1"
                    value={selected.forwardYaw ?? 0}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      updateSelected({
                        forwardYaw: Number.isFinite(v) ? ((v % 360) + 360) % 360 : 0,
                      });
                    }}
                  />
                </div>

                {/* Aim helpers — these are the recommended workflow. The
                    author rarely cares about the exact yaw number; they
                    just want the pill arrow to point at something
                    meaningful (a POI, where they're standing, etc.) and
                    the renderer + editor handle the math. */}
                <div style={{ ...styles.row, marginTop: 6 }}>
                  <button
                    type="button"
                    style={{ ...styles.btn, ...styles.btnPrimary, flex: 1, padding: '6px 8px', fontSize: 11 }}
                    title="Point arrow toward the linked Target POI in 3D space"
                    disabled={!selected.targetSid}
                    onClick={() => {
                      const list = navHintsService.getAll();
                      const poi = list.find((p) => p.id === selected.targetSid);
                      if (!poi || !poi.position) {
                        showToast('Pick a Target POI first');
                        return;
                      }
                      const dx = poi.position.x - selected.position.x;
                      const dz = poi.position.z - selected.position.z;
                      if (Math.hypot(dx, dz) < 0.01) {
                        showToast('Target POI is on top of the pill');
                        return;
                      }
                      // Matterport yaw convention used elsewhere in
                      // the codebase: `y = atan2(-dx, -dz)` faces
                      // (dx, dz). See matterportService.js _walkPath.
                      let y = Math.atan2(-dx, -dz) * 180 / Math.PI;
                      y = ((y % 360) + 360) % 360;
                      updateSelected({ forwardYaw: parseFloat(y.toFixed(2)) });
                      showToast('Arrow aimed at target POI');
                    }}
                  >🎯 Aim at target POI</button>
                </div>
                <div style={{ ...styles.row, marginTop: 4 }}>
                  <button
                    type="button"
                    style={{ ...styles.btn, ...styles.btnSecondary, flex: 1, padding: '6px 8px', fontSize: 11 }}
                    title="Point arrow in the same direction the camera is currently facing"
                    onClick={() => {
                      const pose = matterportService.getCurrentPose();
                      if (!pose?.rotation || pose.rotation.y == null) {
                        showToast('No camera pose yet');
                        return;
                      }
                      let y = pose.rotation.y;
                      y = ((y % 360) + 360) % 360;
                      updateSelected({ forwardYaw: parseFloat(y.toFixed(2)) });
                      showToast('Arrow aimed in camera direction');
                    }}
                  >📷 Aim at camera direction</button>
                  <button
                    type="button"
                    style={{ ...styles.btn, ...styles.btnSecondary, padding: '6px 8px', fontSize: 11 }}
                    title="Clear — fall back to legacy auto-rotate-toward-target behaviour"
                    onClick={() => updateSelected({
                      forwardYaw: null,
                      // Drop any stale legacy fields so they don't
                      // accidentally re-engage at render time.
                      directionDeg: null,
                      directionAuthorYaw: null,
                      directionLabelKey: '',
                    })}
                  >Auto</button>
                </div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                  Tip: stand where the user will arrive, face the next
                  destination, then click 📷. The arrow now points there
                  in 3D and the label updates as the user looks around.
                </div>

                {/* ── Custom destination name (optional, all langs) ── */}
                <label style={styles.label}>Custom destination name</label>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>
                  Optional. Overrides the auto pill name (which mirrors
                  the linked Target POI). Use this when the pill should
                  read differently from the POI itself. Fill any
                  language; blanks fall back to the POI's name.
                </div>
                {LANGUAGES.map((l) => (
                  <div key={l} style={{ marginBottom: 6 }}>
                    <div style={{
                      fontSize: 10, color: 'rgba(255,215,0,0.75)',
                      marginBottom: 2, fontWeight: 700,
                    }}>{LANG_LABELS[l]}</div>
                    <input
                      style={styles.input}
                      type="text"
                      placeholder="(use Target POI's name)"
                      value={selected.customName?.[l] || ''}
                      onChange={(e) => updateSelected({
                        customName: { [l]: e.target.value },
                      })}
                    />
                  </div>
                ))}
              </>
            )}

            {selected.type !== 'pill' && selected.type !== 'groundArrow' && (
              <>
                <label style={styles.label}>Label ({LANG_LABELS[editLang]})</label>
                <input
                  style={styles.input}
                  type="text"
                  value={selected.labels?.[editLang] || ''}
                  onChange={(e) => updateSelected({ labels: { [editLang]: e.target.value } })}
                />
              </>
            )}
            {selected.type === 'pill' && (
              <div style={{
                fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 4,
                padding: '6px 10px', background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6,
              }}>
                Label auto-syncs with the selected Target POI's name.
              </div>
            )}

            {(selected.type === 'poi') && (
              <>
                <label style={styles.label}>Visibility cone</label>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>
                  Restrict the POI to be visible only when the camera is on a
                  particular side of it. Useful when the label is on the front
                  of a building and shouldn't follow the user around the back.
                </div>
                <div style={styles.row}>
                  <button
                    type="button"
                    style={{ ...styles.btn, ...styles.btnSecondary, flex: 1 }}
                    title="Capture the camera's current yaw as the direction the POI faces"
                    onClick={() => {
                      const pose = matterportService.getCurrentPose();
                      if (!pose || !pose.position) return;
                      // POI's "front" should point toward where the camera
                      // currently stands — i.e. yaw of (camera→POI is irrelevant);
                      // we want the POI to face the camera, so capture the
                      // direction from POI to camera and convert to yaw.
                      const dx = pose.position.x - (selected.position?.x ?? 0);
                      const dz = pose.position.z - (selected.position?.z ?? 0);
                      // Inverse of (sin y, -cos y): atan2(dx, -dz).
                      const yawRad = Math.atan2(dx, -dz);
                      let yawDeg = (yawRad * 180) / Math.PI;
                      yawDeg = ((yawDeg % 360) + 360) % 360;
                      updateSelected({
                        viewYaw: parseFloat(yawDeg.toFixed(1)),
                        viewArc: selected.viewArc === 360 ? 180 : selected.viewArc,
                      });
                    }}
                  >📷 Aim at camera</button>
                  <button
                    type="button"
                    style={{
                      ...styles.btn, ...styles.btnSecondary, padding: '4px 10px',
                      opacity: selected.viewYaw != null ? 1 : 0.4,
                    }}
                    disabled={selected.viewYaw == null}
                    title="Reset to omnidirectional"
                    onClick={() => updateSelected({ viewYaw: null, viewArc: 360 })}
                  >↺</button>
                </div>
                {selected.viewYaw != null && (
                  <>
                    <label style={{ ...styles.label, marginTop: 6 }}>
                      Front yaw: {Math.round(((selected.viewYaw % 360) + 360) % 360)}°
                    </label>
                    <SliderInput
                      type="range"
                      min="0" max="360" step="1"
                      value={((selected.viewYaw % 360) + 360) % 360}
                      onChange={(e) => updateSelected({ viewYaw: parseFloat(e.target.value) })}
                      style={{ width: '100%' }}
                    />
                    <label style={{ ...styles.label, marginTop: 6 }}>
                      Visible arc: {Math.round(selected.viewArc ?? 360)}°
                      {selected.viewArc >= 360 ? ' (omnidirectional)' : ''}
                    </label>
                    <SliderInput
                      type="range"
                      min="30" max="360" step="5"
                      value={selected.viewArc ?? 360}
                      onChange={(e) => updateSelected({ viewArc: parseFloat(e.target.value) })}
                      style={{ width: '100%' }}
                    />
                  </>
                )}
                <div style={{ ...styles.separator, marginTop: 10, marginBottom: 10 }} />

                <label style={styles.label}>Click-to-play voiceover</label>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>
                  Adds a speaker icon next to the POI label. Clicking plays
                  the language-specific guided-tour narration from
                  <code style={{ marginLeft: 4 }}>/assets/audio/narration/&lt;stop&gt;_standalone_&lt;lang&gt;.mp3</code>.
                </div>
                <div style={styles.row}>
                  <select
                    style={{ ...styles.input, flex: 1 }}
                    value={selected.voiceoverTagId || ''}
                    onChange={(e) => updateSelected({ voiceoverTagId: e.target.value })}
                  >
                    <option value="">— None —</option>
                    {VOICEOVER_TAG_OPTIONS.map((opt) => (
                      <option key={opt.sid} value={opt.sid}>
                        {opt.label} ({opt.sid})
                      </option>
                    ))}
                  </select>
                  {selected.voiceoverTagId && (
                    <button
                      type="button"
                      style={{ ...styles.btn, ...styles.btnSecondary, padding: '4px 10px' }}
                      title={`Preview voiceover (${LANG_LABELS[editLang]})`}
                      onClick={() => {
                        if (voiceoverService.isPlaying()
                            && voiceoverService._currentTagId === selected.voiceoverTagId) {
                          voiceoverService.stop();
                        } else {
                          voiceoverService.playForTag(selected.voiceoverTagId, editLang);
                        }
                      }}
                    >▶</button>
                  )}
                </div>

                {selected.voiceoverTagId && (
                  <div style={{
                    marginTop: 10, padding: 8,
                    background: 'rgba(255,180,80,0.06)',
                    border: '1px solid rgba(255,180,80,0.25)',
                    borderRadius: 6,
                  }}>
                    <label style={{ ...styles.row, alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={!!selected.autoVoiceover}
                        onChange={(e) => updateSelected({ autoVoiceover: e.target.checked })}
                      />
                      <span style={{ flex: 1, fontSize: 12, color: 'rgba(255,220,180,0.95)' }}>
                        🚶 Auto-play on approach
                      </span>
                    </label>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>
                      Narration starts when the user reaches one of the
                      trigger points below AND the camera is facing within
                      ±30° of the captured direction. Manual walking only —
                      not during quick-nav or guided tours. Background music
                      ducks to ~10%.
                    </div>
                    {selected.autoVoiceover && (() => {
                      const tps = Array.isArray(selected.triggerPoints) ? selected.triggerPoints : [];
                      const captureCurrent = () => {
                        const pose = matterportService.getCurrentPose();
                        if (!pose || !pose.position) {
                          // eslint-disable-next-line no-alert
                          alert('Camera pose not available yet — move in the scene first.');
                          return;
                        }
                        const newPoint = {
                          position: {
                            x: parseFloat((pose.position.x || 0).toFixed(3)),
                            y: parseFloat((pose.position.y || 0).toFixed(3)),
                            z: parseFloat((pose.position.z || 0).toFixed(3)),
                          },
                          cameraYawDeg: parseFloat(((pose.rotation?.y || 0)).toFixed(1)),
                        };
                        updateSelected({ triggerPoints: [...tps, newPoint] });
                      };
                      const removeAt = (idx) => {
                        const next = tps.slice();
                        next.splice(idx, 1);
                        updateSelected({ triggerPoints: next });
                      };
                      const updateAt = (idx, patch) => {
                        const next = tps.slice();
                        next[idx] = {
                          ...next[idx],
                          ...patch,
                          position: { ...next[idx].position, ...(patch.position || {}) },
                        };
                        updateSelected({ triggerPoints: next });
                      };
                      return (
                        <div style={{ marginTop: 8 }}>
                          <div style={{ ...styles.row, alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ ...styles.label, margin: 0 }}>
                              Trigger points ({tps.length})
                            </span>
                            <button
                              type="button"
                              style={{ ...styles.btn, ...styles.btnSecondary, padding: '4px 8px', fontSize: 11 }}
                              title="Walk to the spot, face the POI, then click to capture"
                              onClick={captureCurrent}
                            >📍 Capture current pose</button>
                          </div>
                          {tps.length === 0 && (
                            <div style={{ fontSize: 10, color: 'rgba(255,180,80,0.75)', marginTop: 6 }}>
                              No trigger points yet. Walk in the scene to the
                              exact spot and direction where the narration
                              should start, then click "Capture current pose".
                              Add multiple if the POI should trigger from
                              different angles.
                            </div>
                          )}
                          {tps.map((tp, idx) => (
                            <div
                              key={idx}
                              style={{
                                marginTop: 6, padding: 6,
                                background: 'rgba(0,0,0,0.25)',
                                border: '1px solid rgba(255,255,255,0.08)',
                                borderRadius: 4,
                              }}
                            >
                              <div style={{ ...styles.row, alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                                <span style={{ fontSize: 11, color: 'rgba(255,220,180,0.85)' }}>
                                  #{idx + 1} · yaw {Math.round(tp.cameraYawDeg || 0)}° (±30°)
                                </span>
                                <div style={{ display: 'flex', gap: 4 }}>
                                  <button
                                    type="button"
                                    style={{ ...styles.btn, ...styles.btnSecondary, padding: '2px 6px', fontSize: 10 }}
                                    title="Fly to this trigger point"
                                    onClick={() => {
                                      const p = tp.position || {};
                                      try {
                                        matterportService.navigateToPosition(p.x || 0, p.y || 0, p.z || 0);
                                      } catch (_) { /* no-op */ }
                                    }}
                                  >▶ Go</button>
                                  <button
                                    type="button"
                                    style={{ ...styles.btn, ...styles.btnSecondary, padding: '2px 6px', fontSize: 10 }}
                                    title="Remove this trigger point"
                                    onClick={() => removeAt(idx)}
                                  >🗑️</button>
                                </div>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
                                {['x', 'y', 'z'].map((axis) => (
                                  <input
                                    key={axis}
                                    type="number"
                                    step="0.01"
                                    style={{ ...styles.input, fontSize: 10, padding: 3 }}
                                    value={tp.position?.[axis] ?? 0}
                                    onChange={(e) => updateAt(idx, { position: { [axis]: parseFloat(e.target.value) || 0 } })}
                                    title={`Position ${axis}`}
                                  />
                                ))}
                                <input
                                  type="number"
                                  step="1"
                                  style={{ ...styles.input, fontSize: 10, padding: 3 }}
                                  value={tp.cameraYawDeg ?? 0}
                                  onChange={(e) => updateAt(idx, { cameraYawDeg: parseFloat(e.target.value) || 0 })}
                                  title="Camera yaw (degrees)"
                                />
                              </div>
                            </div>
                          ))}
                          {tps.length === 0 && (
                            <details style={{ marginTop: 8 }}>
                              <summary style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}>
                                Legacy radius/arc fallback
                              </summary>
                              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>
                                When no trigger points are defined, this POI
                                falls back to the old radius + facing-arc
                                test. Capture at least one trigger point to
                                use the precise mode.
                              </div>
                              <label style={{ ...styles.label, marginTop: 6 }}>
                                Trigger radius: {(selected.autoVoiceoverRadius ?? 3).toFixed(1)} m
                              </label>
                              <SliderInput
                                type="range"
                                min="0.5" max="10" step="0.25"
                                value={selected.autoVoiceoverRadius ?? 3}
                                onChange={(e) => updateSelected({ autoVoiceoverRadius: parseFloat(e.target.value) })}
                                style={{ width: '100%' }}
                              />
                              <label style={{ ...styles.label, marginTop: 6 }}>
                                Facing arc: {Math.round(selected.autoVoiceoverFacingArc ?? 90)}°
                              </label>
                              <SliderInput
                                type="range"
                                min="20" max="270" step="5"
                                value={selected.autoVoiceoverFacingArc ?? 90}
                                onChange={(e) => updateSelected({ autoVoiceoverFacingArc: parseFloat(e.target.value) })}
                                style={{ width: '100%' }}
                              />
                            </details>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* ── Gallery photos (POI only) ─────────────────────────── */}
                <label style={{ ...styles.label, marginTop: 10 }}>Gallery photos (optional)</label>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>
                  Curated tiles shown in the side panel while the viewer is
                  inside the gallery radius. Hidden during guided tours.
                  Each image is embedded as a data URL.
                </div>
                <div style={styles.row}>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ ...styles.input, padding: 6, fontSize: 11 }}
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (!files.length) return;
                      const MAX = 1.5 * 1024 * 1024; // 1.5 MB per image
                      const tooBig = files.find((f) => f.size > MAX);
                      if (tooBig) {
                        showToast(`Image too large (max 1.5 MB): ${tooBig.name}`);
                        e.target.value = '';
                        return;
                      }
                      Promise.all(files.map((f) => new Promise((res, rej) => {
                        const r = new FileReader();
                        r.onload = () => res({ src: r.result, caption: { mr: '', en: '', hi: '' } });
                        r.onerror = () => rej();
                        r.readAsDataURL(f);
                      })))
                        .then((items) => {
                          const existing = Array.isArray(selected.gallery) ? selected.gallery : [];
                          updateSelected({ gallery: [...existing, ...items] });
                          showToast(`Added ${items.length} photo${items.length === 1 ? '' : 's'}`);
                        })
                        .catch(() => showToast('Image read failed'));
                      e.target.value = '';
                    }}
                  />
                </div>
                {/* ── Add from POI library (auto-listed from disk) ── */}
                {poiManifest && Object.keys(poiManifest).length > 0 && (
                  <div style={{
                    marginTop: 8,
                    padding: 8,
                    border: '1px solid rgba(255,215,0,0.15)',
                    borderRadius: 6,
                    background: 'rgba(255,255,255,0.02)',
                  }}>
                    <div style={{ ...styles.label, marginTop: 0 }}>Add from POI library</div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>
                      Pick a folder under
                      <code style={{ marginLeft: 4, fontSize: 10 }}>public/assets/images/pois/</code>
                      to add all its images to the album. Stored as paths
                      (no embed), so they don't bloat the JSON.
                    </div>
                    <select
                      style={{ ...styles.input, fontSize: 11 }}
                      defaultValue=""
                      onChange={(e) => {
                        const slug = e.target.value;
                        if (!slug) return;
                        const files = poiManifest[slug] || [];
                        if (!files.length) {
                          showToast('Folder is empty');
                          e.target.value = '';
                          return;
                        }
                        const existing = Array.isArray(selected.gallery) ? selected.gallery : [];
                        const have = new Set(existing.map((it) => it.src));
                        const adds = files.filter((s) => !have.has(s)).map((src) => ({
                          src, caption: { mr: '', en: '', hi: '' },
                        }));
                        if (!adds.length) {
                          showToast('All already added');
                          e.target.value = '';
                          return;
                        }
                        updateSelected({ gallery: [...existing, ...adds] });
                        showToast(`Added ${adds.length} from ${slug}/`);
                        e.target.value = '';
                      }}
                    >
                      <option value="">— select a folder to add —</option>
                      {Object.entries(poiManifest)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([slug, files]) => (
                          <option key={slug} value={slug}>
                            {slug}/ ({files.length})
                          </option>
                        ))}
                    </select>
                  </div>
                )}
                {Array.isArray(selected.gallery) && selected.gallery.length > 0 && (
                  <>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: 6,
                      marginTop: 8,
                    }}>
                      {selected.gallery.map((item, idx) => (
                        <div
                          key={idx}
                          style={{
                            position: 'relative',
                            border: '1px solid rgba(255,255,255,0.15)',
                            borderRadius: 4,
                            overflow: 'hidden',
                            background: 'rgba(0,0,0,0.3)',
                          }}
                        >
                          <img
                            src={item.src}
                            alt={item.caption?.en || `Photo ${idx + 1}`}
                            style={{ width: '100%', height: 56, objectFit: 'cover', display: 'block' }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const next = selected.gallery.filter((_, i) => i !== idx);
                              updateSelected({ gallery: next });
                            }}
                            title="Remove"
                            style={{
                              position: 'absolute', top: 2, right: 2,
                              width: 18, height: 18, lineHeight: '14px',
                              padding: 0, fontSize: 12,
                              background: 'rgba(180,40,40,0.9)',
                              color: '#fff', border: 'none', borderRadius: 3,
                              cursor: 'pointer',
                            }}
                          >✕</button>
                          <input
                            type="text"
                            placeholder="caption (en)"
                            value={item.caption?.en || ''}
                            onChange={(ev) => {
                              const next = [...selected.gallery];
                              next[idx] = {
                                ...item,
                                caption: { ...(item.caption || {}), en: ev.target.value },
                              };
                              updateSelected({ gallery: next });
                            }}
                            style={{
                              width: '100%', boxSizing: 'border-box',
                              padding: '2px 4px', fontSize: 10,
                              background: 'rgba(0,0,0,0.5)', color: '#fff',
                              border: 'none', borderTop: '1px solid rgba(255,255,255,0.1)',
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    <label style={{ ...styles.label, marginTop: 8 }}>
                      Gallery radius: {(selected.galleryRadius ?? 6).toFixed(1)} m
                    </label>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                      Tiles appear when within radius and disappear when the
                      viewer moves outside it.
                    </div>
                    <SliderInput
                      type="range"
                      min="1" max="20" step="0.5"
                      value={selected.galleryRadius ?? 6}
                      onChange={(e) => updateSelected({ galleryRadius: parseFloat(e.target.value) })}
                      style={{ width: '100%' }}
                    />
                    {/* ── Camera focus location (sweep trigger) ── */}
                    <label style={{ ...styles.label, marginTop: 10 }}>Camera focus (sweep)</label>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                      Optional. When set, the gallery shows ONLY when the visitor is on this sweep —
                      radius is ignored. Leave blank to use radius proximity.
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        type="text"
                        placeholder="(none — uses radius)"
                        value={selected.gallerySweepId || ''}
                        onChange={(e) => updateSelected({ gallerySweepId: e.target.value.trim() })}
                        style={{ ...styles.input, fontFamily: 'monospace', fontSize: 11 }}
                      />
                      <button
                        type="button"
                        title="Use the sweep the camera is on right now"
                        onClick={() => {
                          const sid = matterportService.getCurrentSweepId?.() || '';
                          if (!sid) { showToast('No current sweep'); return; }
                          updateSelected({ gallerySweepId: sid });
                          showToast('Sweep captured');
                        }}
                        style={{ ...styles.btn, ...styles.btnSecondary, whiteSpace: 'nowrap' }}
                      >📍 Use current</button>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateSelected({ gallery: [] })}
                      style={{ ...styles.btn, ...styles.btnDanger, marginTop: 6, width: '100%' }}
                    >Clear all gallery photos</button>
                  </>
                )}
              </>
            )}

            {selected.type === 'billboard' && (
              <>
                <div style={styles.separator} />
                <div style={{
                  fontSize: 11, color: 'rgba(255,200,140,0.85)', marginBottom: 8,
                  padding: '6px 10px', background: 'rgba(255,170,80,0.08)',
                  border: '1px solid rgba(255,170,80,0.25)', borderRadius: 6,
                }}>
                  🪧 World-anchored signage — fixed yaw, never rotates to camera.
                </div>

                <label style={styles.label}>Board shape (top design)</label>
                <select
                  style={styles.input}
                  value={selected.boardShape || 'rect'}
                  onChange={(e) => updateSelected({ boardShape: e.target.value })}
                >
                  {BILLBOARD_BOARD_SHAPES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>

                <label style={styles.label}>Base stand</label>
                <select
                  style={styles.input}
                  value={selected.baseStand || 'singlePost'}
                  onChange={(e) => updateSelected({ baseStand: e.target.value })}
                >
                  {BILLBOARD_BASE_STANDS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>

                <label style={styles.label}>
                  Facing direction (yaw): {Math.round(((selected.rotationY ?? 0) % 360 + 360) % 360)}°
                </label>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                  World-space yaw — the board's front face points this way and never moves again.
                </div>
                <div style={styles.row}>
                  <SliderInput
                    type="range"
                    min="0" max="360" step="1"
                    value={((selected.rotationY ?? 0) % 360 + 360) % 360}
                    onChange={(e) => updateSelected({ rotationY: parseFloat(e.target.value) })}
                    style={{ flex: 1 }}
                  />
                  <input
                    style={{ ...styles.numInput, width: 60 }}
                    type="number"
                    step="1"
                    value={((selected.rotationY ?? 0) % 360 + 360) % 360}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      updateSelected({
                        rotationY: Number.isFinite(v) ? ((v % 360) + 360) % 360 : 0,
                      });
                    }}
                  />
                </div>
                <div style={{ ...styles.row, marginTop: 6 }}>
                  <button
                    type="button"
                    style={{ ...styles.btn, ...styles.btnSecondary, flex: 1, padding: '6px 8px', fontSize: 11 }}
                    title="Make the board face the current camera direction"
                    onClick={() => {
                      const pose = matterportService.getCurrentPose();
                      if (pose?.rotation?.y == null) {
                        showToast('No camera pose yet');
                        return;
                      }
                      let y = pose.rotation.y + 180;
                      y = ((y % 360) + 360) % 360;
                      updateSelected({ rotationY: parseFloat(y.toFixed(2)) });
                      showToast('Billboard now faces camera');
                    }}
                  >📷 Face the camera</button>
                </div>

                <label style={styles.label}>
                  Scale: ×{(selected.scale ?? 1).toFixed(2)}
                </label>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                  At ×1 the board is ≈ 1.6 m × 1 m on a 1.4 m post (real-world sized).
                </div>
                <SliderInput
                  type="range"
                  min="0.3" max="3" step="0.05"
                  value={selected.scale ?? 1}
                  onChange={(e) => updateSelected({ scale: parseFloat(e.target.value) })}
                  style={{ width: '100%' }}
                />

                <label style={styles.label}>Board colour</label>
                <div style={styles.row}>
                  <input
                    style={styles.colorInput}
                    type="color"
                    value={selected.boardColor || '#FFFFFF'}
                    onChange={(e) => updateSelected({ boardColor: e.target.value })}
                  />
                  <input
                    style={{ ...styles.input, flex: 1 }}
                    type="text"
                    value={selected.boardColor || ''}
                    onChange={(e) => updateSelected({ boardColor: e.target.value })}
                  />
                </div>

                <label style={styles.label}>Text colour</label>
                <div style={styles.row}>
                  <input
                    style={styles.colorInput}
                    type="color"
                    value={selected.boardTextColor || '#111111'}
                    onChange={(e) => updateSelected({ boardTextColor: e.target.value })}
                  />
                  <input
                    style={{ ...styles.input, flex: 1 }}
                    type="text"
                    value={selected.boardTextColor || ''}
                    onChange={(e) => updateSelected({ boardTextColor: e.target.value })}
                  />
                </div>

                <label style={styles.label}>Border colour</label>
                <div style={styles.row}>
                  <input
                    style={styles.colorInput}
                    type="color"
                    value={selected.boardBorderColor || '#111111'}
                    onChange={(e) => updateSelected({ boardBorderColor: e.target.value })}
                  />
                  <input
                    style={{ ...styles.input, flex: 1 }}
                    type="text"
                    value={selected.boardBorderColor || ''}
                    onChange={(e) => updateSelected({ boardBorderColor: e.target.value })}
                  />
                </div>

                <label style={styles.label}>Stand / post colour</label>
                <div style={styles.row}>
                  <input
                    style={styles.colorInput}
                    type="color"
                    value={selected.standColor || '#7d7d7d'}
                    onChange={(e) => updateSelected({ standColor: e.target.value })}
                  />
                  <input
                    style={{ ...styles.input, flex: 1 }}
                    type="text"
                    value={selected.standColor || ''}
                    onChange={(e) => updateSelected({ standColor: e.target.value })}
                  />
                </div>
              </>
            )}

            {selected.type === 'directionSign' && (
              <>
                <div style={styles.separator} />
                <div style={{
                  fontSize: 11, color: 'rgba(220,180,255,0.85)', marginBottom: 8,
                  padding: '6px 10px', background: 'rgba(200,140,255,0.08)',
                  border: '1px solid rgba(200,140,255,0.25)', borderRadius: 6,
                }}>
                  🧭 Multi-arm direction signpost — pick a visual variant, then add arms (each with its own world-space yaw + label).
                </div>

                <label style={styles.label}>Visual variant</label>
                <select
                  style={styles.input}
                  value={selected.signVariant || 'A'}
                  onChange={(e) => updateSelected({ signVariant: e.target.value })}
                >
                  {DIRECTION_SIGN_VARIANTS.map((v) => (
                    <option key={v.value} value={v.value}>{v.label}</option>
                  ))}
                </select>

                <label style={styles.label}>
                  Base rotation (yaw): {Math.round(((selected.rotationY ?? 0) % 360 + 360) % 360)}°
                </label>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                  Rotates the entire post (and all arms with it). Each arm also has its own absolute yaw below.
                </div>
                <div style={styles.row}>
                  <SliderInput
                    type="range"
                    min="0" max="360" step="1"
                    value={((selected.rotationY ?? 0) % 360 + 360) % 360}
                    onChange={(e) => updateSelected({ rotationY: parseFloat(e.target.value) })}
                    style={{ flex: 1 }}
                  />
                  <input
                    style={{ ...styles.numInput, width: 60 }}
                    type="number"
                    step="1"
                    value={((selected.rotationY ?? 0) % 360 + 360) % 360}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      updateSelected({
                        rotationY: Number.isFinite(v) ? ((v % 360) + 360) % 360 : 0,
                      });
                    }}
                  />
                </div>

                <label style={styles.label}>
                  Scale: ×{(selected.scale ?? 1).toFixed(2)}
                </label>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                  At ×1 the post is ≈ 2.6 m tall with 1.4 m × 0.34 m arms.
                </div>
                <SliderInput
                  type="range"
                  min="0.3" max="3" step="0.05"
                  value={selected.scale ?? 1}
                  onChange={(e) => updateSelected({ scale: parseFloat(e.target.value) })}
                  style={{ width: '100%' }}
                />

                <div style={{ ...styles.separator, marginTop: 14 }} />
                <label style={styles.label}>Customize colours & font</label>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>
                  Per-hint overrides on top of the variant palette. Click ↺ to revert a colour to the variant default.
                </div>
                {[
                  { key: 'signFill',        label: 'Arm background',  fallback: '#fdfcf6' },
                  { key: 'signTextColor',   label: 'Label text',      fallback: '#1a120e' },
                  { key: 'signBorderColor', label: 'Arm border',      fallback: '#1a120e' },
                  { key: 'signAccentColor', label: 'Arrow accent',    fallback: '#c9933a' },
                  { key: 'signPostColor',   label: 'Post (pole)',     fallback: '#1a120e' },
                  { key: 'signBrassColor',  label: 'Brass clamp/cap', fallback: '#c9933a' },
                ].map(({ key, label, fallback }) => {
                  const v = selected[key] || '';
                  const overridden = v !== '';
                  return (
                    <div key={key} style={{ ...styles.row, marginBottom: 4, alignItems: 'center' }}>
                      <span style={{ flex: 1, fontSize: 11, color: 'rgba(255,255,255,0.75)' }}>
                        {label}
                      </span>
                      <input
                        type="color"
                        value={v || fallback}
                        onChange={(e) => updateSelected({ [key]: e.target.value })}
                        title={overridden ? `Override: ${v}` : `Variant default: ${fallback}`}
                        style={{
                          width: 36, height: 24, padding: 0, border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: 4, background: 'transparent', cursor: 'pointer',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => updateSelected({ [key]: '' })}
                        disabled={!overridden}
                        title="Reset to variant default"
                        style={{
                          width: 24, height: 24, padding: 0,
                          background: overridden ? 'rgba(255,255,255,0.08)' : 'transparent',
                          border: '1px solid rgba(255,255,255,0.15)',
                          borderRadius: 4,
                          color: overridden ? '#fff' : 'rgba(255,255,255,0.3)',
                          cursor: overridden ? 'pointer' : 'default',
                          fontSize: 12,
                        }}
                      >↺</button>
                    </div>
                  );
                })}

                <label style={{ ...styles.label, marginTop: 8 }}>
                  Font size: ×{(selected.signFontScale ?? 1).toFixed(2)}
                </label>
                <div style={styles.row}>
                  <SliderInput
                    type="range"
                    min="0.6" max="1.4" step="0.05"
                    value={selected.signFontScale ?? 1}
                    onChange={(e) => updateSelected({ signFontScale: parseFloat(e.target.value) })}
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => updateSelected({ signFontScale: 1.0 })}
                    title="Reset font scale"
                    style={{
                      width: 24, height: 24, padding: 0,
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: 4, color: '#fff', cursor: 'pointer', fontSize: 12,
                    }}
                  >↺</button>
                </div>

                <div style={{ ...styles.separator, marginTop: 14 }} />
                <label style={styles.label}>
                  Arms ({(selected.signArms || []).length})
                </label>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>
                  Each arm is a directional pointer. Yaw is world-space (0° = north, 90° = east). Up to 4 arms recommended.
                </div>

                {(selected.signArms || []).map((arm, idx) => {
                  const armYaw = ((arm.yaw ?? 0) % 360 + 360) % 360;
                  // Resolve the linked POI (if any). When the arm has a
                  // targetId AND no explicit `customLabels`, the POI's
                  // labels are used as the displayed destination. The
                  // author can still tick "Custom labels" to override.
                  const targetPoi = arm.targetId
                    ? hints.find((h) => h.id === arm.targetId && h.type === 'poi')
                    : null;
                  const useCustom = !arm.targetId || arm.customLabels === true;
                  const effectiveLabels = useCustom
                    ? (arm.labels || {})
                    : (targetPoi?.labels || arm.labels || {});
                  return (
                    <div key={idx} style={{
                      padding: 8, marginBottom: 8,
                      background: 'rgba(200,140,255,0.06)',
                      border: '1px solid rgba(200,140,255,0.18)',
                      borderRadius: 8,
                    }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        marginBottom: 6,
                      }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(220,180,255,0.9)' }}>
                          Arm #{idx + 1}
                        </span>
                        <button
                          type="button"
                          style={{ ...styles.btn, ...styles.btnDanger, padding: '2px 8px', fontSize: 10 }}
                          onClick={() => {
                            const next = [...(selected.signArms || [])];
                            next.splice(idx, 1);
                            updateSelected({ signArms: next });
                          }}
                        >Remove</button>
                      </div>

                      {/* Target POI dropdown — pick a POI to auto-fill labels */}
                      <label style={{ ...styles.label, fontSize: 9 }}>Target POI</label>
                      <select
                        style={styles.input}
                        value={arm.targetId || ''}
                        onChange={(e) => {
                          const next = [...(selected.signArms || [])];
                          const id = e.target.value || null;
                          next[idx] = { ...next[idx], targetId: id };
                          // When clearing the target, drop the customLabels
                          // override flag so the arm uses its own labels.
                          if (!id) delete next[idx].customLabels;
                          updateSelected({ signArms: next });
                        }}
                      >
                        <option value="">— custom destination —</option>
                        {hints
                          .filter((h) => h.type === 'poi')
                          .map((h) => {
                            // Editor dropdowns always show English names.
                            const name =
                              h.labels?.en
                              || h.labels?.mr
                              || h.labels?.hi
                              || `POI ${h.id}`;
                            return (
                              <option key={h.id} value={h.id}>{name}</option>
                            );
                          })}
                      </select>

                      {arm.targetId && (
                        <label style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          fontSize: 10, color: 'rgba(255,255,255,0.7)', marginTop: 4,
                        }}>
                          <input
                            type="checkbox"
                            checked={arm.customLabels === true}
                            onChange={(e) => {
                              const next = [...(selected.signArms || [])];
                              next[idx] = { ...next[idx], customLabels: e.target.checked };
                              updateSelected({ signArms: next });
                            }}
                          />
                          Override with custom labels
                        </label>
                      )}

                      {LANGUAGES.map((lang) => (
                        <div key={lang} style={{ marginBottom: 4 }}>
                          <label style={{ ...styles.label, marginTop: 4, fontSize: 9 }}>
                            {LANG_LABELS[lang]}
                            {!useCustom && (
                              <span style={{ marginLeft: 6, opacity: 0.55, fontWeight: 400 }}>
                                (from POI)
                              </span>
                            )}
                          </label>
                          <input
                            style={{
                              ...styles.input,
                              ...(useCustom ? null : { opacity: 0.7, cursor: 'not-allowed' }),
                            }}
                            type="text"
                            disabled={!useCustom}
                            value={effectiveLabels?.[lang] || ''}
                            placeholder={!useCustom ? '(synced from Target POI)' : ''}
                            onChange={(e) => {
                              if (!useCustom) return;
                              const next = [...(selected.signArms || [])];
                              next[idx] = {
                                ...next[idx],
                                labels: { ...next[idx].labels, [lang]: e.target.value },
                              };
                              updateSelected({ signArms: next });
                            }}
                          />
                        </div>
                      ))}

                      <label style={{ ...styles.label, fontSize: 9, marginTop: 6 }}>Layout</label>
                      <div style={{ ...styles.row, gap: 6 }}>
                        {[
                          { value: 'auto', label: 'Auto' },
                          { value: '1',    label: '1 line' },
                          { value: '2',    label: '2 lines' },
                        ].map((opt) => {
                          const cur = arm.lineMode || 'auto';
                          const active = cur === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              style={{
                                ...styles.btn,
                                ...(active ? styles.btnPrimary : styles.btnSecondary),
                                flex: 1,
                                padding: '4px 6px',
                                fontSize: 10,
                              }}
                              onClick={() => {
                                const next = [...(selected.signArms || [])];
                                next[idx] = { ...next[idx], lineMode: opt.value };
                                if (opt.value === 'auto') delete next[idx].lineMode;
                                updateSelected({ signArms: next });
                              }}
                            >{opt.label}</button>
                          );
                        })}
                      </div>

                      <label style={{ ...styles.label, fontSize: 9, marginTop: 6 }}>
                        Font size (px)
                        {!(Number.isFinite(arm.fontSizePx) && arm.fontSizePx > 0) && (
                          <span style={{ marginLeft: 6, opacity: 0.55, fontWeight: 400 }}>
                            (auto)
                          </span>
                        )}
                      </label>
                      {(() => {
                        // When no override is set, show the auto-fit
                        // size that the renderer would actually use —
                        // makes the input a useful starting point for
                        // manual tweaks instead of always reading "0".
                        const autoSize = (() => {
                          try {
                            return navDirectionSignService.getAutoArmFontSize(
                              selected, idx, editLang
                            ) || 0;
                          } catch { return 0; }
                        })();
                        const overridden = Number.isFinite(arm.fontSizePx) && arm.fontSizePx > 0;
                        const displayed = overridden ? arm.fontSizePx : autoSize;
                        const setOverride = (raw) => {
                          const v = parseFloat(raw);
                          const next = [...(selected.signArms || [])];
                          if (!Number.isFinite(v) || v <= 0) {
                            const { fontSizePx, ...rest } = next[idx] || {};
                            next[idx] = rest;
                          } else {
                            next[idx] = { ...next[idx], fontSizePx: Math.round(v) };
                          }
                          updateSelected({ signArms: next });
                        };
                        return (
                          <div style={styles.row}>
                            <SliderInput
                              type="range"
                              min="8" max="160" step="1"
                              value={displayed || 40}
                              onChange={(e) => setOverride(e.target.value)}
                              style={{ flex: 1 }}
                            />
                            <input
                              style={{
                                ...styles.numInput,
                                width: 60,
                                ...(overridden ? null : { opacity: 0.7 }),
                              }}
                              type="number"
                              min="0" max="200" step="1"
                              value={displayed}
                              onChange={(e) => setOverride(e.target.value)}
                              title={overridden
                                ? 'Manual override — set 0 to revert to auto'
                                : 'Auto-fit size — type a value to override'}
                            />
                            {overridden && (
                              <button
                                type="button"
                                style={{ ...styles.btn, ...styles.btnSecondary, padding: '4px 8px', fontSize: 10 }}
                                title="Revert to auto-fit size"
                                onClick={() => setOverride('0')}
                              >Auto</button>
                            )}
                          </div>
                        );
                      })()}

                      <label style={{ ...styles.label, fontSize: 9 }}>
                        Yaw: {Math.round(armYaw)}°
                      </label>
                      <div style={styles.row}>
                        <SliderInput
                          type="range"
                          min="0" max="360" step="1"
                          value={armYaw}
                          onChange={(e) => {
                            const next = [...(selected.signArms || [])];
                            next[idx] = { ...next[idx], yaw: parseFloat(e.target.value) };
                            updateSelected({ signArms: next });
                          }}
                          style={{ flex: 1 }}
                        />
                        <input
                          style={{ ...styles.numInput, width: 60 }}
                          type="number"
                          step="1"
                          value={armYaw}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value);
                            const yaw = Number.isFinite(v) ? ((v % 360) + 360) % 360 : 0;
                            const next = [...(selected.signArms || [])];
                            next[idx] = { ...next[idx], yaw };
                            updateSelected({ signArms: next });
                          }}
                        />
                        <button
                          type="button"
                          style={{ ...styles.btn, ...styles.btnSecondary, padding: '4px 8px', fontSize: 10 }}
                          title="Aim this arm in the camera's current direction"
                          onClick={() => {
                            const pose = matterportService.getCurrentPose();
                            if (pose?.rotation?.y == null) {
                              showToast('No camera pose yet');
                              return;
                            }
                            const yaw = ((pose.rotation.y % 360) + 360) % 360;
                            const next = [...(selected.signArms || [])];
                            next[idx] = { ...next[idx], yaw: parseFloat(yaw.toFixed(2)) };
                            updateSelected({ signArms: next });
                          }}
                        >📷</button>
                      </div>
                    </div>
                  );
                })}

                <button
                  type="button"
                  style={{ ...styles.btn, ...styles.btnSecondary, width: '100%', padding: '8px 6px', fontSize: 11 }}
                  disabled={(selected.signArms || []).length >= 6}
                  onClick={() => {
                    const next = [...(selected.signArms || [])];
                    // Stagger the new arm 90° from the last so the author
                    // gets visible separation immediately.
                    const lastYaw = next.length > 0 ? (next[next.length - 1].yaw ?? 0) : 0;
                    next.push({
                      labels: { mr: '', en: 'New destination', hi: '' },
                      yaw: (lastYaw + 90) % 360,
                    });
                    updateSelected({ signArms: next });
                  }}
                >+ Add arm</button>
              </>
            )}

            {selected.type === 'groundArrow' && (
              <>
                <div style={styles.separator} />
                <label style={styles.label}>Heading: {((selected.forwardYaw ?? 0)).toFixed(0)}°</label>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                  World-space yaw the chevrons point toward (0° = north / −Z, increases clockwise from above).
                </div>
                <div style={styles.row}>
                  <SliderInput
                    type="range"
                    min="0" max="360" step="1"
                    value={selected.forwardYaw ?? 0}
                    onChange={(e) => updateSelected({ forwardYaw: parseFloat(e.target.value) })}
                    style={{ flex: 1 }}
                  />
                  <input
                    style={{ ...styles.numInput, width: 60 }}
                    type="number" step="1"
                    value={selected.forwardYaw ?? 0}
                    onChange={(e) => updateSelected({ forwardYaw: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div style={{ ...styles.row, marginTop: 4 }}>
                  <button
                    type="button"
                    style={{ ...styles.btn, ...styles.btnSecondary, flex: 1, fontSize: 11 }}
                    onClick={() => {
                      // Aim the arrow head AWAY from the camera through
                      // the hint position. The strip then lies along
                      // the camera-to-anchor line — visually a straight
                      // forward arrow from the visitor's POV (no
                      // diagonal stripe). Also re-capture the approach
                      // vector so the gate matches the new orientation.
                      const pose = matterportService.getCurrentPose();
                      if (!pose?.position) { showToast('No camera pose'); return; }
                      const dx = selected.position.x - pose.position.x;
                      const dz = selected.position.z - pose.position.z;
                      const len = Math.sqrt(dx * dx + dz * dz);
                      if (len < 0.1) { showToast('Stand farther from the arrow'); return; }
                      let yaw = (Math.atan2(dx, -dz) * 180) / Math.PI;
                      yaw = ((yaw % 360) + 360) % 360;
                      updateSelected({
                        forwardYaw: parseFloat(yaw.toFixed(2)),
                        approachVector: {
                          x: parseFloat((dx / len).toFixed(3)),
                          z: parseFloat((dz / len).toFixed(3)),
                        },
                      });
                      showToast('Aimed away from camera');
                    }}
                  >📍 Point away from camera</button>
                </div>

                <label style={styles.label}>Length: {(selected.arrowLength ?? 2.5).toFixed(1)} m</label>
                <SliderInput
                  type="range"
                  min="0.5" max="6" step="0.1"
                  value={selected.arrowLength ?? 2.5}
                  onChange={(e) => updateSelected({ arrowLength: parseFloat(e.target.value) })}
                  style={{ width: '100%' }}
                />
                <label style={styles.label}>
                  Turn: {((selected.turnAngle ?? 0)) > 0 ? '↻ ' : ((selected.turnAngle ?? 0)) < 0 ? '↺ ' : ''}
                  {(selected.turnAngle ?? 0).toFixed(0)}°
                </label>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                  Curve the strip into a left/right turn. 0° = straight,
                  +90° = right turn, −90° = left turn. Useful for
                  signage at corners.
                </div>
                <div style={styles.row}>
                  <SliderInput
                    type="range"
                    min="-180" max="180" step="5"
                    value={selected.turnAngle ?? 0}
                    onChange={(e) => updateSelected({ turnAngle: parseFloat(e.target.value) })}
                    style={{ flex: 1 }}
                  />
                  <input
                    style={{ ...styles.numInput, width: 60 }}
                    type="number" step="5"
                    value={selected.turnAngle ?? 0}
                    onChange={(e) => updateSelected({ turnAngle: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div style={{ ...styles.row, marginTop: 4, gap: 4 }}>
                  <button
                    type="button"
                    style={{ ...styles.btn, ...styles.btnSecondary, flex: 1, fontSize: 11 }}
                    onClick={() => updateSelected({ turnAngle: -90 })}
                    title="Curve the strip into a left turn"
                  >↺ Left 90°</button>
                  <button
                    type="button"
                    style={{ ...styles.btn, ...styles.btnSecondary, flex: 1, fontSize: 11 }}
                    onClick={() => updateSelected({ turnAngle: 0 })}
                    title="Reset to a straight strip"
                  >Straight</button>
                  <button
                    type="button"
                    style={{ ...styles.btn, ...styles.btnSecondary, flex: 1, fontSize: 11 }}
                    onClick={() => updateSelected({ turnAngle: 90 })}
                    title="Curve the strip into a right turn"
                  >↻ Right 90°</button>
                </div>

                {/* ── Target POI + label (per-arrow) ──────────────── */}
                {(() => {
                  const targetPoi = selected.targetId
                    ? hints.find((h) => h.id === selected.targetId && h.type === 'poi')
                    : null;
                  const useCustom = !selected.targetId || selected.customLabels === true;
                  const effectiveLabels = useCustom
                    ? (selected.labels || {})
                    : (targetPoi?.labels || selected.labels || {});
                  return (
                    <>
                      <label style={styles.label}>Target POI</label>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                        Pick a POI to auto-fill the arrow's label from
                        its name. Tick "Override with custom labels" to
                        type a different destination name.
                      </div>
                      <select
                        style={styles.input}
                        value={selected.targetId || ''}
                        onChange={(e) => {
                          const id = e.target.value || null;
                          const patch = { targetId: id };
                          // Drop the override flag when clearing the
                          // target so the arrow uses its own labels.
                          if (!id) patch.customLabels = false;
                          updateSelected(patch);
                        }}
                      >
                        <option value="">— custom destination —</option>
                        {hints
                          .filter((h) => h.type === 'poi')
                          .map((h) => {
                            const name =
                              h.labels?.en
                              || h.labels?.mr
                              || h.labels?.hi
                              || `POI ${h.id}`;
                            return (
                              <option key={h.id} value={h.id}>{name}</option>
                            );
                          })}
                      </select>

                      {selected.targetId && (
                        <label style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          fontSize: 10, color: 'rgba(255,255,255,0.7)', marginTop: 4,
                        }}>
                          <input
                            type="checkbox"
                            checked={selected.customLabels === true}
                            onChange={(e) => updateSelected({ customLabels: e.target.checked })}
                          />
                          Override with custom labels
                        </label>
                      )}

                      {LANGUAGES.map((lang) => (
                        <div key={lang} style={{ marginBottom: 4 }}>
                          <label style={{ ...styles.label, marginTop: 4, fontSize: 9 }}>
                            {LANG_LABELS[lang]}
                            {!useCustom && (
                              <span style={{ marginLeft: 6, opacity: 0.55, fontWeight: 400 }}>
                                (from POI)
                              </span>
                            )}
                          </label>
                          <input
                            style={{
                              ...styles.input,
                              ...(useCustom ? null : { opacity: 0.7, cursor: 'not-allowed' }),
                            }}
                            type="text"
                            disabled={!useCustom}
                            value={effectiveLabels?.[lang] || ''}
                            placeholder={!useCustom ? '(synced from Target POI)' : ''}
                            onChange={(e) => {
                              if (!useCustom) return;
                              updateSelected({ labels: { [lang]: e.target.value } });
                            }}
                          />
                        </div>
                      ))}
                    </>
                  );
                })()}

                <label style={styles.label}>Label lines</label>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                  Wrap the label onto two lines for long destinations.
                  Two-line mode auto-splits at the most balanced
                  whitespace; insert a literal <code>\n</code> in the
                  label text to control the break point.
                </div>
                <div style={{ ...styles.row, gap: 4 }}>
                  {[
                    { v: 'auto', label: 'Auto (1 line)' },
                    { v: '1', label: 'Single line' },
                    { v: '2', label: 'Two lines' },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      style={{
                        ...styles.btn,
                        ...((selected.labelLineMode || 'auto') === opt.v ? styles.btnPrimary : styles.btnSecondary),
                        flex: 1, padding: '6px 4px', fontSize: 11,
                      }}
                      onClick={() => updateSelected({ labelLineMode: opt.v })}
                    >{opt.label}</button>
                  ))}
                </div>
                <div style={{
                  marginTop: 8, padding: '6px 8px', borderRadius: 4,
                  background: 'rgba(255,215,0,0.06)',
                  border: '1px dashed rgba(255,215,0,0.3)',
                  fontSize: 10, color: 'rgba(255,255,255,0.7)', lineHeight: 1.4,
                }}>
                  Visual style (variant, colour, width, breaks count, flow,
                  label size, highlight…) is shared across <em>all</em>
                  ground arrows. Edit it under <strong>⚙ Global settings →
                  Ground arrow</strong>.
                </div>
              </>
            )}

            <label style={styles.label}>Position (X, Y, Z)</label>
            <div style={styles.row}>
              {['x', 'y', 'z'].map((k) => (
                <input
                  key={k}
                  style={styles.numInput}
                  type="number"
                  step="0.05"
                  value={selected.position[k]}
                  onChange={(e) => updateSelected({
                    position: { [k]: parseFloat(e.target.value) || 0 },
                  })}
                />
              ))}
            </div>
            <div style={{ ...styles.row, marginTop: 4 }}>
              <button
                type="button"
                style={{ ...styles.btn, ...styles.btnSecondary, flex: 1, fontSize: 11 }}
                onClick={() => {
                  setAddMode(false);
                  setMoveMode((v) => !v);
                }}
                title={selected.type === 'directionSign'
                  ? 'Click on the ground to move — Y snaps to floor automatically'
                  : 'Click anywhere in the scene to move this hint'}
              >{moveMode ? 'Cancel move' : '🎯 Move on map'}</button>
            </div>

            <label style={styles.label}>
              Vertical height: {(selected.offsetY || 0).toFixed(2)} m
            </label>
            <div style={styles.row}>
              <SliderInput
                type="range"
                min="-3" max="3" step="0.05"
                value={selected.offsetY || 0}
                onChange={(e) => updateSelected({ offsetY: parseFloat(e.target.value) })}
                style={{ flex: 1 }}
              />
              <input
                style={{ ...styles.numInput, width: 70 }}
                type="number"
                step="0.05"
                value={selected.offsetY || 0}
                onChange={(e) => updateSelected({ offsetY: parseFloat(e.target.value) || 0 })}
              />
              <button
                type="button"
                onClick={() => updateSelected({ offsetY: 1 })}
                style={{ ...styles.btn, ...styles.btnSecondary, padding: '4px 8px', fontSize: 10 }}
                title="Reset to 1m"
              >↺</button>
            </div>

            <div style={styles.separator} />
            {selected.type !== 'billboard' && selected.type !== 'directionSign' && (
              <>
            <label style={styles.label}>Visibility</label>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>
              Shows only when user is within range & (optionally) approaching from the captured direction.
            </div>
            <label style={styles.label}>
              Max distance: {(selected.maxDistance ?? 6).toFixed(1)} m
            </label>
            <SliderInput
              type="range"
              min="1" max="15" step="0.5"
              value={selected.maxDistance ?? 6}
              onChange={(e) => updateSelected({ maxDistance: parseFloat(e.target.value) })}
              style={{ width: '100%' }}
            />

            <label style={styles.label}>
              Fade start: {(selected.fadeStart ?? 0).toFixed(1)} m (0 = no fade)
            </label>
            <SliderInput
              type="range"
              min="0" max={selected.maxDistance ?? 6} step="0.5"
              value={selected.fadeStart ?? 0}
              onChange={(e) => updateSelected({ fadeStart: parseFloat(e.target.value) })}
              style={{ width: '100%' }}
            />

            {selected.type === 'pill' && (
              <>
                <label style={styles.label}>
                  Hide when within: {(selected.hideWithin ?? 0).toFixed(1)} m (0 = never hide)
                </label>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
                  Pill disappears once the viewer reaches this radius — keeps it from nagging "go right" when they're already there.
                </div>
                <SliderInput
                  type="range"
                  min="0" max="5" step="0.1"
                  value={selected.hideWithin ?? 0}
                  onChange={(e) => updateSelected({ hideWithin: parseFloat(e.target.value) })}
                  style={{ width: '100%' }}
                />
              </>
            )}

            <label style={styles.label}>Approach direction</label>
            {selected.type === 'poi' ? (
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>
                POI markers are always omnidirectional — visible from any side within range.
              </div>
            ) : (
              <>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>
                  {selected.approachVector
                    ? `Directional — from (${selected.approachVector.x.toFixed(2)}, ${selected.approachVector.z.toFixed(2)})`
                    : 'Omni — visible from any side within range'}
                </div>
                <div style={styles.row}>
                  <button
                    type="button"
                    style={{ ...styles.btn, ...styles.btnPrimary, flex: 1 }}
                    onClick={captureApproachDirection}
                    title="Stand where the user should be, then click"
                  >📍 Capture from camera</button>
                  <button
                    type="button"
                    style={{ ...styles.btn, ...styles.btnSecondary }}
                    onClick={clearApproachDirection}
                    disabled={!selected.approachVector}
                  >Omni</button>
                </div>
                {selected.approachVector && (
                  <>
                    <label style={styles.label}>
                      Cone width: {Math.round(Math.acos(Math.max(-1, Math.min(1, selected.approachThreshold ?? 0.3))) * 180 / Math.PI)}°
                    </label>
                    <SliderInput
                      type="range"
                      min="-0.5" max="0.95" step="0.05"
                      value={selected.approachThreshold ?? 0.3}
                      onChange={(e) => updateSelected({ approachThreshold: parseFloat(e.target.value) })}
                      style={{ width: '100%' }}
                    />
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
                      Narrower cone = stricter direction match.
                    </div>
                  </>
                )}
              </>
            )}
              </>
            )}
          </>
        )}
      </div>
      {(addMode || moveMode) && (
        <div
          style={styles.captureOverlay}
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onClick={handleOverlayClick}
          title={moveMode ? 'Click to move hint' : 'Click to place hint'}
        />
      )}
      {toast && <div style={styles.toast}>{toast}</div>}
    </>
  );
};

export default NavHintsEditor;
